import { MANAGED_PAGES, labelKey, tileKey } from "../pages/registry";
import {
  FORM_PAGE,
  INFOS_PAGE,
  formIdKey,
  htmlKey,
  pageOfInstance,
  subtitleKey,
} from "../pages/repeatable";
import { REPEATABLE_PAGES } from "../pages/registry";
import { DEFAULT_BRAND, DEFAULT_PAGE } from "../theme/palette";
import { FUNNEL, stepShowKey } from "../routes/registry";
import { REGIMES, REGIMES_KEY, regimeLabelKey } from "../funnel/regimes";
import {
  ORDER_KEY,
  FIRST_INSTANCE,
  instanceKey,
  type Entry,
} from "../funnel/order";
import type {
  CatalogContext,
  CatalogScope,
  Group,
  Origin,
  Setting,
} from "./types";

/**
 * LA LARGEUR D'UN BANDEAU ENVOYÉ, en pixels : un mur en 1920, un écran haute densité de
 * portable (1 440 × 2) ou un écran QHD s'y retrouvent sans flou. Au-delà, on ne paie que
 * du poids. Voir `maxWidth` (`types.ts`).
 */
const BAND_MAX_WIDTH = 2560;

/**
 * L'étape répétable, prise au registre plutôt que réécrite : son interrupteur se
 * construit à partir d'elle (`stepShowKey`), et une clé recopiée ici finirait par
 * diverger de la sienne.
 */
const INFORMATIONS_SCREEN = FUNNEL.find((s) => s.key === "informations")!;

/**
 * ═══ LES FORMULAIRES QU'UN SÉLECTEUR PROPOSE, RANGÉS PAR PROVENANCE ═══
 *
 * ⚠️ LA ROUTE REND DEUX FAMILLES DANS UNE SEULE LISTE : ceux de l'ÉVÉNEMENT et ceux du
 * DOMAINE, réutilisables d'un événement à l'autre. Mélangés, deux « Inscription » se
 * ressemblent à s'y méprendre — et le second n'a pas les mêmes conséquences : modifier
 * un formulaire du domaine touche TOUS les événements qui le servent. Le groupe le dit à
 * la lecture, sans note à côté.
 *
 * ⚠️ CEUX DE L'ÉVÉNEMENT D'ABORD, comme la route les trie déjà : c'est le cas courant, et
 * c'est celui qu'on peut modifier sans conséquence ailleurs.
 */
function formsOf(
  forms: CatalogContext["forms"],
  type: "U" | "C",
): Array<{ value: string; label: string; group: string }> {
  const rowClass = [...forms.filter((f) => f.type === type)].sort(
    (a, b) => Number(a.eventId === null) - Number(b.eventId === null),
  );
  return rowClass.map((f) => ({
    value: String(f.id),
    label: f.name,
    group:
      f.eventId === null
        ? "Formulaires du domaine"
        : "Formulaires de cet événement",
  }));
}

/**
 * LES DEUX NOMS DE PAGE QUE LA V1 SAIT DÉJÀ DONNER.
 *
 * ⚠️ ILS VIVAIENT DANS `REPRISES_DE_PAGE`, la liste réservée à la reprise, parce que ces
 * clés n'étaient déclarées nulle part ailleurs. Maintenant qu'elles le sont, les laisser
 * là-bas les aurait fait proposer DEUX FOIS par la reprise.
 */
const NAME_ORIGIN: Record<string, Origin | undefined> = {
  contacts: {
    key: "FO_MEETING_USER_LINKS_BUTTON_CONTACTS_TEXT",
    note: "Vérifié : le titre de la tuile « Voir mes contacts » dans `screens/Home.jsx` de la V1.",
  },
};

/**
 * THE CATALOG — the list of settings, as DATA.
 *
 * The panel is GENERATED from this file; it is never hand-written. Adding a
 * setting means adding an entry, not touching the interface.
 *
 * Two families, exactly the agreed framing:
 *   — `site`   : what applies everywhere (identity, pages, footer…)
 *   — per page : what belongs to the screen on display
 *
 * ⚠️ EVERY KEY IS PREFIXED `FO_MEETING_V2_`, AND THAT IS NOT COSMETIC. V1 is
 * live and serving events from `FO_MEETING_*`. Sharing those keys would mean an
 * organiser opening V2 to try something silently rewrites the site participants
 * are using today — and the two fronts would fight over every value for as long
 * as they coexist. Distinct namespaces, so configuring one never touches the
 * other.
 *
 * The consequence is deliberate: on an existing event V2 starts EMPTY. Nothing
 * is inherited from V1, because inheriting means sharing.
 *
 * A misspelled key breaks nothing — it silently falls back to the inherited
 * value — hence strict mode and this typed catalog.
 *
 * Labels and help text are French: they are shown to the organiser.
 *
 * ⚠️ A RULE TO KEEP AS THIS FILE GROWS. A setting that only means something when
 * another is on must declare it with `needs`. The label of a button is nothing
 * without the button; the footer's details are nothing without the footer. Left
 * on screen they read as editable, and an organiser spends an afternoon on a
 * field that governs nothing. The panel hides them and says how many, and why.
 *
 * This file will grow to the ~250 settings surveyed in the product. It currently
 * carries enough to exercise the whole mechanism on the home screen.
 */
/**
 * LE PARCOURS, RÉGLÉ EN UN SEUL ENDROIT.
 *
 * Une famille présente sur CHAQUE écran du tunnel : quelles étapes un participant
 * traverse, et comment chacune s'appelle. Deux raisons de la faire ainsi :
 *
 *   — La leçon du gestionnaire « Pages du site » du prototype : un interrupteur ici
 *     et un libellé là, ça se contredit. Le nom de l'étape ne vit donc QUE dans ce
 *     groupe, pas aussi dans le groupe de l'étape elle-même.
 *   — Le fil d'Ariane ne montre que le parcours réel. Sans ce groupe, une étape
 *     retirée n'était réglable nulle part — il fallait la deviner.
 *
 * ⚠️ LA CONFIRMATION N'A PAS D'INTERRUPTEUR. C'est là que les réponses sont ÉCRITES :
 * la retirer laisserait un tunnel qui demande tout et n'enregistre rien, et la faute
 * ne se verrait qu'à la porte. Sa ligne reste, pour son nom, et l'aide le dit.
 */
const FUNNEL_GROUP: Group = {
  title: "Étapes du parcours",
  help: "Ce qu'un participant traverse, et dans quel ordre. Une étape retirée disparaît du fil d'Ariane et n'est plus servie ; elle reste configurable par le sélecteur d'écran, en haut.",
  /**
   * ⚠️ UNE FONCTION DEPUIS QUE L'ORDRE SE RÈGLE. Cette famille dépendait du seul REGISTRE,
   * qui est statique ; elle dépend maintenant de ce que l'organisateur a rangé — l'ordre
   * des étapes, et le nombre d'instances de celles qui se répètent. Le panneau apporte la
   * liste (`steps`), comme il apporte déjà les typologies et les formulaires.
   *
   * ⚠️ LE GESTIONNAIRE D'ABORD, LES NOMS ENSUITE. Une ligne par étape avec ses flèches,
   * puis le nom de chacune : la question « dans quel ordre ? » et la question « comment
   * l'appelle-t-on ? » ne se posent pas au même moment, et les mêler ferait une liste de
   * vingt lignes où l'ordre ne se lit plus.
   */
  settings: ({ steps }) => {
    const entries =
      steps && steps.length > 0
        ? steps
        : FUNNEL.map((s) => ({ key: s.key, instance: undefined }));

    /**
     * ⚠️ LES INTERRUPTEURS RESTENT DÉCLARÉS ICI, et les retirer aurait vidé le parcours.
     * `activeSteps` lit l'état EFFECTIF d'une étape par `defaultOnOf`, qui cherche la clé
     * dans CE catalogue : une clé qu'il n'y trouve plus vaut « éteinte », donc toutes les
     * étapes retirables auraient disparu du fil d'un coup. Le gestionnaire s'occupe de
     * l'ORDRE et du NOMBRE ; la présence garde son interrupteur.
     */
    const named = entries.flatMap((entry, i) => {
      const step = FUNNEL.find((s) => s.key === entry.key);
      if (!step) return [];
      /* Deux étapes du même nom se numérotent, sinon leurs réglages sont impossibles à
         distinguer dans le panneau. */
      const howMany = entries.filter((e) => e.key === entry.key).length;
      const rank =
        howMany > 1
          ? ` ${entries.slice(0, i + 1).filter((e) => e.key === entry.key).length}`
          : "";
      const title = `${step.stepName}${rank}`;
      return [
        ...(step.removable
          ? [
              {
                key: stepShowKey(step, entry.instance),
                label: "Servie",
                section: title,
                type: "boolean" as const,
                /* Portée par la ligne de l'étape, dans le parcours juste au-dessus. */
                managed: true as const,
                /* Allumée tant que personne ne l'éteint — SAUF celle qu'on ajoute : voir
                   `optIn` dans le registre, qui dit pourquoi la société est la seule dans
                   ce cas. */
                onByDefault: !step.optIn,
                target: "steps",
                // La correspondance V1 est déclarée sur le REGISTRE, seul endroit où
                // l'étape existe une fois ; elle voyage jusqu'ici avec elle. Une instance
                // ajoutée, elle, n'a aucun équivalent dans la V1.
                ...(step.origin && !entry.instance
                  ? { origin: step.origin }
                  : {}),
              },
            ]
          : []),
        {
          key: instanceKey(step.funnel!, entry.instance),
          label: "Nom de l'étape",
          section: title,
          type: "text" as const,
          managed: true as const,
          ...(step.removable
            ? { needs: stepShowKey(step, entry.instance) }
            : {}),
          fallback: step.stepName!,
          target: "steps",
          help: step.removable
            ? "LE MOT DE VOTRE ÉVÉNEMENT pour cette notion. Il sert partout où le site la désigne : le fil d'Ariane et le récapitulatif de la confirmation."
            : "Cette étape ne se retire pas : c'est là que les réponses sont enregistrées.",
        },
      ];
    });

    return [
      {
        key: ORDER_KEY,
        label: "Le parcours",
        type: "steps" as const,
        translatable: false,
        target: "steps",
        help: "Montez ou descendez une étape pour changer l'ordre. Certaines ne se déplacent pas : le parcours le dit quand c'est le cas, et pourquoi.",
      },
      ...named,
    ];
  },
};

/**
 * ⚠️ LA CLÉ EST CELLE DE LA V2, ET ICI LA RÈGLE DU PRÉFIXE S'APPLIQUE PLEINEMENT —
 * contrairement aux traductions de formulaire, qu'on partage volontairement. Ce CSS
 * vise `[data-cfg]` et les classes de la V2, qui n'existent PAS dans la V1 ; et
 * `FO_MEETING_CUSTOM_CSS` vise celles de la V1, qui n'existent pas ici. Partager la
 * clé injecterait dans chaque front des sélecteurs qui n'y correspondent à rien.
 */
export const CUSTOM_CSS_KEY = "FO_MEETING_V2_CUSTOM_CSS";

/**
 * Range une liste de réglages sous UNE section, qui remplace la leur.
 *
 * Sert aux familles faites de plusieurs anciennes familles (« Apparence ») : chaque
 * ancienne famille devient une section, et ses propres sous-titres s'effacent devant
 * elle. Deux niveaux de sous-titres dans un panneau de 380 px ne se lisent plus.
 */
function inSection(
  section: string,
  settings: ReadonlyArray<Setting>,
): Setting[] {
  return settings.map((setting) => ({ ...setting, section }));
}

export const CATALOG: Record<CatalogScope, ReadonlyArray<Group>> = {
  site: [
    /**
     * ═══ L'ÉVÉNEMENT : CE QUI LE DÉSIGNE PARTOUT ═══
     *
     * Le nom, l'icône de l'onglet et les langues : rien de tout cela ne vit sur UNE page.
     * Le nom titre le badge et les emails, le favicon ne s'affiche que dans l'onglet, et
     * les langues décident des versions qu'on peut rédiger. Ce qui ne se voit qu'en haut
     * du site (monogramme, bandeau) se règle dans « Apparence ».
     *
     * ⚠️ LES CLÉS NE BOUGENT PAS, seulement leur rangement : « Identité » et « Langues »
     * étaient deux familles, réunies le 2026-10-08 pour que « Le site » se lise dans
     * l'ordre où l'on prépare un événement.
     */
    {
      title: "L'événement",
      help: "Ce qui désigne l'événement partout, et pas seulement sur une page : son nom, repris par le badge et les emails, l'icône de l'onglet, et les langues dans lesquelles le site se lit. Ce qui ne s'affiche qu'en haut du site se règle dans « Apparence ».",
      settings: [
        {
          key: "FO_MEETING_V2_EVENT_NAME",
          fallback: "Événement",
          origin: {
            key: "title",
            note: "L'événement range son nom dans `title`, pas dans une clé `FO_MEETING_*`.",
          },
          // Un nom propre. « Salon Tech 2026 » reste « Salon Tech 2026 ».
          translatable: false,
          label: "Nom de l'événement",
          type: "text",
          target: "identity",
        },
        {
          key: "FO_MEETING_V2_FAVICON",
          origin: {
            key: "favicon",
            note: "Le réglage V1 du même nom, de type `upload` : déclaré dans `api/migrations/20250926052407_SettingsFo-addData.js`, importé au back office par `bo-gayakoa/src/screens2/SettingsFrontOffice/Parameters.jsx` (dossier `front_office/favicons`) et posé dans l'onglet par `fo-meeting/src/appRouter.jsx`. Vérifié dans le CODE de la V1, pas sur l'événement 408 qui ne le porte pas. Voir `v1Keys.ts`.",
          },
          label: "Favicon",
          keywords: ["logo"],
          type: "upload",
          /* ⚠️ PAS DE `target`, comme les couleurs et pour la même raison : ça ne se
             voit nulle part SUR la page. L'onglet du navigateur n'est pas un bloc du
             site, et désigner la marque à sa place allumerait un élément que ce
             réglage ne touche pas. Il se règle depuis le panneau, sous « L'événement ». */
          help: "La petite image de l'onglet du navigateur, reprise dans les favoris. Carrée, et lisible en 32 px : un logo entier n'y est plus qu'une tache. Vide : l'icône par défaut du navigateur.",
        },
        {
          key: "FO_MEETING_V2_LANGUAGES_SHOW",
          origin: {
            key: "FO_MEETING_LANGUAGES_SHOW",
            note: "Même forme : la liste des clés de langue (« FR », « EN »).",
          },
          label: "Langues du site",
          type: "languages",
          help: "La langue de référence ne se retire pas : c'est celle sur laquelle toutes les autres retombent.",
        },
        {
          key: "FO_MEETING_V2_LANGUAGE_DEFAULT",
          origin: {
            key: "FO_MEETING_LANGUAGE_DEFAULT",
          },
          label: "Langue de référence",
          type: "language-default",
          help: "Celle qui sert de repli partout où une traduction manque.",
        },
      ],
    },
    /**
     * ═══ QUAND LES INSCRIPTIONS SONT OUVERTES — LES DEUX PORTES DU PARCOURS ═══
     *
     * ⚠️ ELLES SONT DANS « LE SITE », ET NON DANS L'ÉTAPE DE PARTICIPATION OÙ LA CLÔTURE
     * VIVAIT. Deux raisons, et la seconde est un défaut mesurable :
     *
     *   — ON LES CHERCHE. « À quelle heure ferme-t-on ? » est la question qu'un
     *     organisateur se pose une semaine avant, sans savoir sur quel écran la poser :
     *     il fallait ouvrir l'étape « Participation », déplier « Automatismes », et
     *     personne n'y pensait. Cet onglet-ci est ouvert par défaut, sur n'importe quel
     *     écran du site.
     *   — ELLES NE RÈGLENT PAS CETTE ÉTAPE, ELLES FERMENT LE PARCOURS ENTIER (voir
     *     `funnel/thread.ts`, qui le réduit à son premier écran). Rangées dans la
     *     famille « Automatismes », elles héritaient de son `needs` : sur un événement
     *     dont la question de participation est retirée du parcours — le cas de
     *     l'inscription automatique — la clôture DISPARAISSAIT du panneau alors qu'elle
     *     continuait de verrouiller le site, et rien ne permettait de la rouvrir.
     *
     * ⚠️ LES CLÉS SONT ÉCRITES ICI EN TOUTES LETTRES, comme partout dans ce fichier, et
     * NON importées de `funnel/cloture.ts` : ce module importe déjà `defaultOnOf` d'ici,
     * et se citer l'un l'autre ferait un cycle. C'est `settings/catalog.test.ts` qui
     * tient les deux côtés ensemble, en comparant ces lignes aux constantes exportées.
     */
    {
      title: "Ouverture et clôture des inscriptions",
      help: "Les deux heures entre lesquelles on peut s'inscrire, et ce qu'un visiteur lit en dehors. En dehors, le parcours s'arrête à son premier écran et dit pourquoi, au lieu de laisser remplir un formulaire qui échouera au bout.",
      settings: [
        {
          key: "FO_MEETING_V2_REGISTRATION_OPEN_AT",
          label: "Ouvrir les inscriptions le",
          type: "datetime",
          target: "registrationOpening",
          help: "Avant cette heure, personne ne s'inscrit — pas même par un lien reçu en avance. C'est l'heure DE VOTRE FUSEAU : régler 9 h depuis Paris ouvre à 16 h pour un participant à Séoul, au même instant pour tout le monde. Vide : ouvertes depuis toujours.",
        },
        {
          /**
           * ⚠️ IL NE DÉPEND PAS DE L'HEURE D'OUVERTURE, pour la raison qui a déjà
           * détaché le message de clôture de son interrupteur : `needs` est un ET (voir
           * `visibleSettings`), et un message qu'on ne peut relire qu'en verrouillant
           * son site ne se relit jamais. On l'écrit d'abord, on pose l'heure ensuite.
           */
          key: "FO_MEETING_V2_REGISTRATION_NOT_OPEN_TEXT",
          label: "Message d'attente",
          type: "text",
          multiline: true,
          fallback: "Les inscriptions ne sont pas encore ouvertes.",
          target: "registrationOpening",
          help: "Affiché tant que l'heure d'ouverture n'est pas venue. Dites-y quand revenir : c'est la seule chose que le visiteur cherche.",
        },
        {
          key: "FO_MEETING_V2_REGISTRATION_CLOSED",
          label: "Clore les inscriptions",
          type: "boolean",
          target: "registrationClosing",
          help: "Sur-le-champ, et sans rien effacer : l'heure de clôture ci-dessous reste ce qu'elle est.",
        },
        {
          key: "FO_MEETING_V2_REGISTRATION_CLOSED_AT",
          label: "Clore automatiquement le",
          type: "datetime",
          target: "registrationClosing",
          help: "L'heure passée, le parcours se ferme de lui-même — sans avoir à être devant son écran le jour dit. C'est l'heure DE VOTRE FUSEAU : régler 14 h depuis Paris ferme à 21 h pour un participant à Séoul, au même instant pour tout le monde.",
        },
        {
          /**
           * ⚠️ IL NE DÉPEND PLUS DE L'INTERRUPTEUR, ET C'EST L'ÉCHÉANCE QUI L'EXIGE.
           * `needs` exprime un ET, pas un OU (voir `visibleSettings`) : gardé sur
           * « Clore les inscriptions », le message restait invisible à qui n'avait posé
           * qu'une date — il n'aurait pu le relire qu'en fermant tout de suite, ce
           * qu'il cherchait précisément à éviter. Une ligne de trop sur un événement
           * qui ne clôt rien coûte moins qu'un message qu'on ne peut pas écrire.
           */
          key: "FO_MEETING_V2_REGISTRATION_CLOSED_TEXT",
          label: "Message de clôture",
          type: "text",
          multiline: true,
          fallback: "Les inscriptions à cet événement sont terminées.",
          target: "registrationClosing",
          help: "Affiché dès que l'une ou l'autre des deux clôtures prend effet.",
        },
      ],
    },
    /**
     * ⚠️ LE PARCOURS SE RÈGLE ICI, UNE SEULE FOIS, et plus en tête de chacun des neuf
     * écrans du tunnel. Répété partout, il poussait vers le bas ce qui concernait
     * l'écran regardé ; chaque étape du tunnel le rappelle désormais par une ligne qui
     * mène ici (voir `Panel.tsx`).
     */
    FUNNEL_GROUP,
    {
      title: "Pages du site",
      help: "Chaque page, sur une ligne : son nom, dans le menu ? en tuile sur l'accueil ? Le nom se traduit et se surcharge par typologie comme n'importe quel texte.",
      settings: ({ instance }) => [
        {
          key: "FO_MEETING_V2_PAGES",
          label: "Pages",
          type: "pages" as const,
          // Le réglage ne porte AUCUNE valeur : il en pilote trente-six (trois par
          // page). Sa ligne est le gestionnaire, pas un champ — d'où l'absence de
          // provenance, qui n'aurait rien à désigner.
          target: "homeTiles",
        },
        /**
         * ═══ LE NOM DE CHAQUE PAGE ═══
         *
         * ⚠️ IL N'ÉTAIT DÉCLARÉ NULLE PART, et l'aide de cette famille promettait
         * pourtant qu'il « se règle sur la page elle-même ». Il ne s'y réglait pas :
         * aucune portée de page ne portait sa ligne. Renommer « Médias » en « Vos
         * factures » était donc impossible depuis le panneau, sur toutes les pages.
         *
         * ⚠️ DÉCLARÉ ICI, PORTÉ PAR LE GESTIONNAIRE (`managed`) — la ligne de la page
         * l'affiche déjà, la répéter en dessous ferait quatorze champs sous le tableau
         * qui les pilote, exactement la contradiction que ce gestionnaire supprime. Mais
         * déclaré, il l'est : c'est ce qui donne son repli au menu, son intitulé au
         * récapitulatif et sa prose à « Tout traduire ».
         */
        ...MANAGED_PAGES.map((page) => ({
          key: labelKey(page),
          label: `Nom · ${page.name}`,
          type: "text" as const,
          fallback: page.name,
          managed: true as const,
          target: "homeTiles",
          ...(NAME_ORIGIN[page.key] ? { origin: NAME_ORIGIN[page.key]! } : {}),
        })),
        /**
         * ⚠️ ET CELUI DE CHAQUE PAGE AJOUTÉE — section comme page formulaire —,
         * engendré comme tout ce qui n'existe qu'avec l'événement : une passe
         * d'instance le fait apparaître (`everySetting`), sinon son repli, son
         * intitulé au récapitulatif et sa place dans « Tout traduire » manquaient :
         * le piège des clés engendrées, une famille de plus.
         *
         * ⚠️ TOUTES LES FAMILLES À CHAQUE PASSE, et c'est la même règle assumée qu'au
         * dessus : le rang « 2 » engendre le nom de la section 2 ET celui du
         * formulaire 2, même quand une seule existe. La clé de trop est inerte.
         */
        ...(instance
          ? REPEATABLE_PAGES.map((base) => {
              const page = pageOfInstance(base, instance);
              return {
                key: labelKey(page),
                label: `Nom · ${page.name}`,
                type: "text" as const,
                fallback: page.name,
                managed: true as const,
                target: "homeTiles",
              };
            })
          : []),
      ],
    },
    /**
     * ═══ L'APPARENCE : À QUOI RESSEMBLE LE SITE ═══
     *
     * Quatre anciennes familles réunies le 2026-10-08 (« Couleurs », « Haut de page »,
     * « Bandeau du pied de page », « Apparence sur mesure ») : toutes répondent à la même
     * question, et la liste de « Le site » tombait de neuf familles à sept.
     *
     * ⚠️ CHACUNE GARDE SON NOM, EN SECTION, et la carte d'un bloc le reprend : cliquer le
     * bandeau du haut ouvre toujours « Haut de page », pas un « Apparence » qui ne dit
     * pas de quoi il s'agit. Voir `sectioned` dans `types.ts`.
     *
     * ⚠️ LES CLÉS NE BOUGENT PAS. Les raisons des anciens partages tiennent toujours et
     * sont écrites au-dessus de chaque section.
     */
    {
      title: "Apparence",
      help: "Les couleurs du site, le haut et le bas de chaque écran, et les règles demandées bloc par bloc. Le menu se règle dans « Pages du site », les coordonnées du bas dans « Pied de page ».",
      sectioned: true,
      settings: [
        // Pas de `target` pour les couleurs : elles gouvernent TOUT le site, pas un bloc.
        ...inSection("Couleurs", [
          {
            key: "FO_MEETING_V2_BRAND_COLOR",
            /**
             * ⚠️ `bgPrimaryColor`, ET SÛREMENT PAS `FO_PRIMARY_COLOR` — l'erreur a été
             * commise, elle rendait un site vert là où la V1 est orange.
             *
             * Les trois couleurs de la V1 se ressemblent par le nom et ne font pas du
             * tout la même chose :
             *   — `bgPrimaryColor` EST la couleur principale de fo-meeting. Le back
             *     office l'appelle ainsi, dans l'onglet Meeting, et le front en fait le
             *     thème entier.
             *   — `textPrimaryColor` est l'ENCRE POSÉE DESSUS, pas une couleur de
             *     marque. La V2 ne la reprend pas et c'est délibéré : elle CALCULE ce
             *     qui s'écrit sur la marque pour garantir le contraste (`--gk-on-brand`),
             *     et une valeur figée reprise telle quelle rendrait ce calcul inopérant.
             *   — `FO_PRIMARY_COLOR` n'est pas un réglage de ce front.
             */
            origin: {
              key: "bgPrimaryColor",
              note: "Vérifié : `appRouter.jsx:188` la passe à `changeAntdTheme()`, qui repeint TOUT le thème de la V1, et `shared/button.tsx` en fait le fond des boutons. Le back office l'intitule « Couleur primaire (background) » dans l'onglet Meeting (`bo-gayakoa/.../SettingsFrontOffice/Meeting/General.jsx:209`). À ne pas confondre avec `FO_PRIMARY_COLOR`, que fo-meeting ne lit NULLE PART (ses deux occurrences, `screens/Login/index.jsx:105-106`, sont commentées) : elle appartient aux paramètres généraux et sert au back office.",
            },
            label: "Couleur principale",
            type: "color",
            fallback: DEFAULT_BRAND,
            help: "Votre couleur de marque, respectée telle quelle. Ce qui s'écrit dessus s'adapte.",
          },
          {
            key: "FO_MEETING_V2_PAGE_COLOR",
            /**
             * ⚠️ `containerColor`, ET SURTOUT PAS `backgroundColor`.
             *
             * La V1 a deux fonds : `backgroundColor` sur `#root`, et le `.container`
             * par-dessus. Comme sa colonne de contenu est centrée et bornée, le premier
             * ne se voit QUE dans les marges — c'est de la décoration autour d'un
             * châssis que la V2 n'a pas. Repris comme fond de page, un rouge
             * d'événement inondait tout l'écran, cartes comprises.
             *
             * L'équivalent du fond de page V2 est donc le fond du CONTENU, et rien
             * d'autre. `backgroundColor` n'a pas d'équivalent ici : la reprise le laisse
             * dans « non repris » plutôt que de lui inventer une place.
             */
            origin: {
              key: "containerColor",
              note: "Vérifié : `appRouter.jsx` le pose sur `.container` (`background: settings?.containerColor || '#FFFFFF'`), la colonne où vit tout le contenu de la V1. `backgroundColor`, lui, est sur `#root` et ne se voit que dans les marges : ce n'est pas le même rôle.",
            },
            label: "Fond de page",
            type: "color",
            fallback: DEFAULT_PAGE,
            help: "Le fond du contenu. Clair ou sombre : le thème bascule tout seul selon ce que vous posez ici. Avec un dégradé, c'est sa première couleur.",
          },
          /**
           * ═══ LE FOND EN DÉGRADÉ ═══
           *
           * Un interrupteur, puis le style et la seconde couleur. Trois réglages plutôt
           * qu'un style « Uni » parmi les autres : `needs` ne suit qu'un interrupteur, et
           * sans lui la seconde couleur resterait affichée pour un fond uni, où elle ne
           * règle rien. Voir `theme/palette.ts` (`pageBackground`) : la seconde couleur
           * entre aussi dans le calcul du texte, et un dégradé d'une couleur claire vers une
           * sombre ne s'applique pas.
           */
          {
            key: "FO_MEETING_V2_PAGE_GRADIENT",
            label: "Fond en dégradé",
            type: "boolean",
            help: "Le fond de page passe de sa couleur à une seconde. Les cartes restent unies par-dessus, et le texte reste mesuré pour être lisible sur les deux couleurs.",
          },
          {
            key: "FO_MEETING_V2_PAGE_GRADIENT_STYLE",
            label: "Style du dégradé",
            type: "select-text",
            options: [
              { value: "vertical", label: "Vertical doux — du haut vers le bas" },
              { value: "diagonal", label: "Diagonale — de haut-gauche à bas-droite" },
              { value: "halos", label: "Halos — la seconde couleur dans les coins" },
            ],
            fallback: "halos",
            needs: "FO_MEETING_V2_PAGE_GRADIENT",
          },
          {
            key: "FO_MEETING_V2_PAGE_COLOR_2",
            label: "Seconde couleur du dégradé",
            type: "color",
            fallbackFrom: "FO_MEETING_V2_PAGE_COLOR",
            needs: "FO_MEETING_V2_PAGE_GRADIENT",
            help: "Du même côté que le fond de page : deux couleurs claires, ou deux sombres. Une claire et une sombre ne se dégradent pas, aucun texte ne se lirait sur les deux.",
          },
          /**
           * ═══ L'IMAGE DE FOND ═══
           *
           * Sous la couleur du fond — ou son dégradé —, posée en transparence : le voile.
           * Voir `theme/palette.ts` (`VEILS`) : le texte se mesure contre le pire que le
           * voile laisse passer, donc il reste lisible quelle que soit la photo. Réduite à
           * l'envoi comme un bandeau. Le mur de la salle la reprend (`SceneFrame`).
           */
          {
            key: "FO_MEETING_V2_PAGE_IMAGE",
            label: "Image de fond",
            type: "upload",
            maxWidth: BAND_MAX_WIDTH,
            help: "Derrière tout le site, et sur l'écran de salle. Elle couvre la fenêtre et reste fixe quand on fait défiler ; les cartes restent unies par-dessus. Une photo large et calme donne le meilleur résultat.",
          },
          {
            key: "FO_MEETING_V2_PAGE_IMAGE_VEIL",
            label: "Voile sur l'image",
            type: "select-text",
            options: [
              { value: "light", label: "Léger — l'image domine" },
              { value: "medium", label: "Moyen" },
              { value: "strong", label: "Fort — l'image s'efface derrière le texte" },
            ],
            fallback: "medium",
            needs: "FO_MEETING_V2_PAGE_IMAGE",
            help: "La couleur du fond (ou son dégradé), posée en transparence sur l'image. C'est elle qui garde le texte lisible : plus l'image est chargée, plus le voile doit être fort.",
          },
        ]),
        /**
         * ═══ LE HAUT DE LA PAGE, EN FACE DU PIED ═══
         *
         * ⚠️ LES DEUX BOUTS DU SITE NE SE RÉGLAIENT PAS DE LA MÊME FAÇON. Le bas a ses
         * familles depuis toujours — le pied de page et le bandeau qui le surmonte ; le
         * haut, lui, était dispersé dans « Identité », entre le nom de l'événement et un
         * favicon qui ne s'affiche que dans l'onglet du navigateur. Un organisateur qui
         * vient changer le bandeau du haut le cherchait donc sous un titre qui ne parle ni
         * de haut ni de bandeau, et rien dans le panneau ne l'y menait.
         *
         * ⚠️ CE QUI DÉMÉNAGE : ce qui n'existe QUE là-haut. Le monogramme et le libellé de
         * déconnexion ne sont rendus que par la barre (`Shell.tsx`), le bandeau que par le
         * `Band` qui la suit. CE QUI RESTE dans « Identité » sert ailleurs aussi : le nom de
         * l'événement titre le badge (`routes/Badge.tsx`) et les emails, le favicon ne
         * s'affiche sur aucune page. La ligne est là, et non sur « c'est en haut de
         * l'écran » : sinon le nom de l'événement se réglerait dans le haut de page et se
         * lirait sur le badge.
         *
         * ⚠️ LES CLÉS NE BOUGENT PAS, seulement leur rangement. Une famille est un tiroir du
         * panneau ; les valeurs des événements sont rangées sous leur CLÉ. Renommer une clé
         * pour qu'elle ressemble à son nouveau titre aurait vidé le haut de page de tous les
         * événements déjà configurés.
         *
         * ⚠️ LE MENU N'EST PAS ICI, et l'aide le dit plutôt que de laisser chercher : les
         * pages et leur présence dans la barre se règlent d'un seul geste par page dans
         * « Pages du site ». Les dédoubler ici referait la contradiction — un interrupteur
         * d'un côté, un libellé de l'autre — que ce gestionnaire a supprimée.
         */
        ...inSection("Haut de page", [
          {
            key: "FO_MEETING_V2_EVENT_LOGO_TEXT",
            fallback: "••",
            section: "La barre du haut",
            label: "Monogramme",
            keywords: ["logo"],
            type: "text",
            // Initials, not prose. "ST" translated is "ST", and asking costs a
            // request to a model to be told so.
            translatable: false,
            /* ⚠️ LA CIBLE RESTE `identity`, LE BLOC OÙ IL EST RENDU — la marque, où il tient
               compagnie au nom de l'événement. La famille dit où on le RÈGLE, la cible où on
               le VOIT : survoler la marque continue donc d'offrir les deux ensemble. */
            target: "identity",
            help: "Deux ou trois lettres, affichées en haut du site.",
          },
          {
            key: "FO_MEETING_V2_SIGNOUT_LABEL",
            fallback: "Se déconnecter",
            section: "La barre du haut",
            label: "Libellé de déconnexion",
            type: "text",
            target: "signOut",
            help: "Visible seulement pour un participant connecté. Le bouton lui-même ne se retire pas : une session qu'on ne peut pas fermer laisse un compte au suivant qui prend l'appareil.",
          },
          {
            key: "FO_MEETING_V2_HEADER_IMAGE",
            origin: {
              key: "headerImg",
              note: "Champ propre à l'événement, relevé en base sur l'événement 408 : l'adresse S3 du bandeau du haut.",
            },
            section: "Le bandeau",
            label: "Image d'en-tête",
            keywords: ["logo"],
            type: "upload",
            focusKey: "FO_MEETING_V2_HEADER_IMAGE_FOCUS",
            safeKey: "FO_MEETING_V2_HEADER_IMAGE_SAFE",
            maxWidth: BAND_MAX_WIDTH,
            sidesKey: "FO_MEETING_V2_HEADER_IMAGE_SIDES",
            target: "headerImage",
            help: "Bandeau affiché sous la barre du haut, sur toute la largeur : au plus 120 px de haut sur mobile, 180 px sur bureau, sauf hauteur choisie ci-dessous. L'image est RECADRÉE selon la largeur : tracez dans l'aperçu la zone qui doit rester visible en entier (un logo, un titre). Le reste de l'image s'affiche autour s'il y a la place ; s'il manque de l'image sur les côtés, la couleur de son bord est prolongée, et vous pouvez en imposer une autre. Vide : pas de bandeau.",
          },
          {
            key: "FO_MEETING_V2_HEADER_IMAGE_HEIGHT",
            section: "Le bandeau",
            label: "Hauteur du bandeau",
            type: "number",
            fallback: "180",
            target: "headerImage",
            help: "En pixels, sur bureau ; le mobile en prend les deux tiers. Entre 40 et 600. C'est un maximum : si l'image, une fois cadrée, est moins haute, le bandeau prend sa hauteur. Vide : 180 px sur bureau, 120 sur mobile.",
          },
        ]),
        /**
         * ═══ LE BANDEAU DU BAS, SÉPARÉ DES COORDONNÉES QU'IL SUIT ═══
         *
         * ⚠️ UNE IMAGE ET UNE FICHE D'IDENTITÉ NE SE RÈGLENT PAS DANS LE MÊME GESTE, et
         * « Pied de page » tenait les deux. On y venait écrire une adresse ou des dates, et
         * on tombait d'abord sur un envoi de fichier, un recadrage et une hauteur en
         * pixels — trois lignes qui ne disent rien des coordonnées, en tête de la famille
         * qui les porte. Séparées, chaque famille répond à UNE question : à quoi ressemble
         * le bas de l'écran d'un côté, ce qu'on y lit de l'autre.
         *
         * ⚠️ LES CLÉS NE BOUGENT PAS, seulement leur rangement — la même règle que le partage
         * « Identité » / « Haut de page » juste au-dessus. Renommer `FOOTER_IMAGE` pour que
         * la clé ressemble à son nouveau titre aurait vidé le bandeau de tous les événements
         * déjà configurés.
         *
         * ⚠️ ELLE DÉPEND TOUJOURS DE L'INTERRUPTEUR DU PIED DE PAGE, et c'est le rendu qui le
         * commande : `FooterEnd` (`footer/Footer.tsx`) éteint le bandeau AVEC le pied de page.
         * Le bas de l'écran se règle d'un seul geste, même s'il ne se pose pas sur les mêmes
         * écrans : les coordonnées tiennent à l'accueil, l'image et les mentions ferment tous
         * les écrans. Le `needs` traverse
         * donc vers la famille voisine, ce que le panneau sait faire : il retrouve le libellé
         * de l'interrupteur où qu'il soit déclaré (`labelOf`) et l'annonce en toutes lettres.
         * Un interrupteur de plus, propre au bandeau, aurait donné deux façons d'éteindre la
         * même image.
         *
         * ⚠️ ET ELLE PASSE APRÈS « PIED DE PAGE », comme à l'écran : le bandeau ferme la page
         * SOUS les coordonnées, là où il y en a. Une liste de familles qui remonte la page à l'envers se relit
         * à chaque fois — c'est pourquoi elle a changé de place avec le bas de l'écran.
         */
        ...inSection("Bandeau du pied de page", [
          {
            key: "FO_MEETING_V2_FOOTER_IMAGE",
            origin: {
              key: "footerImg",
              note: "Champ propre à l'événement, relevé en base sur l'événement 408 : l'adresse S3 du bandeau du bas.",
            },
            needs: "FO_MEETING_V2_FOOTER_SHOW",
            label: "Image de pied de page",
            type: "upload",
            focusKey: "FO_MEETING_V2_FOOTER_IMAGE_FOCUS",
            safeKey: "FO_MEETING_V2_FOOTER_IMAGE_SAFE",
            maxWidth: BAND_MAX_WIDTH,
            sidesKey: "FO_MEETING_V2_FOOTER_IMAGE_SIDES",
            target: "footerImage",
            help: "Bandeau affiché tout en bas de chaque écran — sous les coordonnées sur la page d'accueil —, sur toute la largeur et cadré exactement comme celui d'en-tête : tracez dans l'aperçu la zone qui doit rester visible en entier. Il disparaît avec le pied de page. Vide : pas de bandeau.",
          },
          {
            key: "FO_MEETING_V2_FOOTER_IMAGE_HEIGHT",
            needs: "FO_MEETING_V2_FOOTER_SHOW",
            label: "Hauteur du bandeau",
            type: "number",
            fallback: "180",
            target: "footerImage",
            help: "En pixels, sur bureau ; le mobile en prend les deux tiers. Entre 40 et 600. C'est un maximum : si l'image, une fois cadrée, est moins haute, le bandeau prend sa hauteur. Vide : 180 px sur bureau, 120 sur mobile.",
          },
        ]),
        ...inSection("Apparence sur mesure", [
          {
            key: CUSTOM_CSS_KEY,
            label: "Règles appliquées",
            type: "css" as const,
            /* Du CSS : `color: red` ne se traduit pas, et le faire passer par un modèle
               produirait une feuille par langue dont l'une casserait le site. */
            translatable: false,
            /* Pas de `target` : ces règles ne désignent pas UN bloc, elles viennent de
               tous. Allumer un bloc du site depuis cette ligne serait arbitraire. */
            help: "Retirez une règle et son bloc reprend son apparence d'origine. Pour en ajouter une, survolez le bloc concerné sur la page et choisissez « Personnaliser l'apparence ».",
          },
        ]),
      ],
    },
    {
      title: "Pied de page",
      /* ⚠️ « SUR LA PAGE D'ACCUEIL », ET C'EST LA PREMIÈRE CHOSE QUE L'AIDE DIT. La
         famille se règle depuis l'onglet « Le site », donc depuis N'IMPORTE QUEL écran :
         sans cette phrase, on écrit ses coordonnées depuis le tunnel, on regarde le bas
         de l'écran qu'on a sous les yeux, et on conclut que le réglage ne marche pas.
         Voir `hasFooter` dans `routes/registry.ts` — c'est le rendu qui commande. */
      help: "Les coordonnées de l'événement — organisateur, dates, lieu, contact —, affichées au bas de la PAGE D'ACCUEIL, connecté comme non connecté, et nulle part ailleurs ; puis les mentions, qui ferment le bas de TOUS les écrans. L'image qui se pose entre les deux se règle dans « Apparence », et s'affiche partout elle aussi.",
      settings: [
        {
          key: "FO_MEETING_V2_FOOTER_SHOW",
          section: "Le pied de page",
          label: "Afficher le pied de page",
          type: "boolean",
          target: "footerDetails",
          help: "Les coordonnées ne s'affichent que sur la page d'accueil, qu'on soit connecté ou non : les autres écrans s'arrêtent à leur contenu, puis à l'image et aux mentions qui les ferment tous. Éteint, cet interrupteur emporte les trois : coordonnées, bandeau du bas et mentions.",
        },
        /* ⚠️ CHAQUE BLOC A DEUX RÉGLAGES : son INTITULÉ et son CONTENU, et ils
           n'obéissent pas à la même règle. L'intitulé est de la langue — « Organisé
           par » se traduit ; le contenu est souvent une donnée — une raison sociale,
           une adresse, un email — qui ne se traduit pas. D'où le `translatable`
           réglé ligne à ligne plutôt qu'une fois pour le groupe.

           Un intitulé vide n'efface pas la colonne : le site retombe sur son
           `fallback`, que le panneau affiche en filigrane. Une colonne sans titre
           serait illisible, et personne ne demande ça en laissant un champ vide. */
        {
          key: "FO_MEETING_V2_FOOTER_ORGANIZER_LABEL",
          needs: "FO_MEETING_V2_FOOTER_SHOW",
          section: "Organisé par",
          label: "Intitulé",
          type: "text",
          fallback: "Organisé par",
          target: "footerDetails",
        },
        {
          key: "FO_MEETING_V2_FOOTER_ORGANIZER",
          // Une raison sociale : elle ne se traduit pas.
          translatable: false,
          needs: "FO_MEETING_V2_FOOTER_SHOW",
          section: "Organisé par",
          label: "Texte",
          type: "text",
          multiline: true,
          target: "footerDetails",
        },
        {
          key: "FO_MEETING_V2_FOOTER_DATES_LABEL",
          needs: "FO_MEETING_V2_FOOTER_SHOW",
          section: "Dates",
          label: "Intitulé",
          type: "text",
          fallback: "Dates",
          target: "footerDetails",
        },
        {
          key: "FO_MEETING_V2_FOOTER_DATES",
          needs: "FO_MEETING_V2_FOOTER_SHOW",
          section: "Dates",
          label: "Texte",
          type: "text",
          multiline: true,
          target: "footerDetails",
        },
        {
          key: "FO_MEETING_V2_FOOTER_PLACE_LABEL",
          needs: "FO_MEETING_V2_FOOTER_SHOW",
          section: "Lieu",
          label: "Intitulé",
          type: "text",
          fallback: "Lieu",
          target: "footerDetails",
        },
        {
          key: "FO_MEETING_V2_EVENT_PLACE",
          // Une adresse postale. « Palais des Congrès » rendu en « Congress
          // Palace » n'est plus trouvable sur une carte.
          translatable: false,
          needs: "FO_MEETING_V2_FOOTER_SHOW",
          section: "Lieu",
          label: "Texte",
          type: "text",
          multiline: true,
          target: "footerDetails",
        },
        /* ⚠️ RANGÉ SOUS « LIEU », PAS DANS UNE SECTION « ACTIONS » : le bouton s'affiche
           dans le bloc du lieu, sous l'adresse (tâche 551, `footer/Footer.tsx`), et le
           réglage suit le bloc qu'il remplit. Juste après le texte du lieu, sinon le
           panneau rouvrirait un second sous-titre « Lieu » plus bas. */
        {
          key: "FO_MEETING_V2_FOOTER_MAP_URL",
          section: "Lieu",
          needs: "FO_MEETING_V2_FOOTER_SHOW",
          label: "Lien d'itinéraire",
          type: "text",
          // An address, not a sentence: nothing to translate, and everything to
          // break by trying.
          translatable: false,
          target: "footerDetails",
          help: "Collez l'adresse de la fiche du lieu depuis Google Maps (le lien « Partager » convient). Le bouton « Itinéraire » s'affiche sous l'adresse du lieu. Vide : pas de bouton.",
        },
        {
          key: "FO_MEETING_V2_FOOTER_CONTACT_LABEL",
          needs: "FO_MEETING_V2_FOOTER_SHOW",
          section: "Contact",
          label: "Intitulé",
          type: "text",
          // Espace insécable avant le « ? » : la typographie française, que la
          // page portait en dur avant que l'intitulé devienne réglable.
          fallback: "Une question ?",
          target: "footerDetails",
        },
        {
          key: "FO_MEETING_V2_FOOTER_CONTACT",
          // Une adresse email et un numéro. Traduits, ils cessent de
          // fonctionner — et personne ne s'en aperçoit avant qu'un participant
          // écrive dans le vide.
          translatable: false,
          needs: "FO_MEETING_V2_FOOTER_SHOW",
          section: "Contact",
          label: "Texte",
          type: "text",
          multiline: true,
          target: "footerDetails",
        },
        /* ⚠️ LES MENTIONS FERMENT LA PAGE, SOUS L'IMAGE DU BANDEAU, ET SUR TOUS LES
           ÉCRANS. C'est la ligne qu'on écrit parce qu'il le faut — mentions légales,
           éditeur du site, numéro de déclaration —, pas celle qu'on vient lire : elle est
           rendue minuscule et serrée, et elle passe après tout le reste. D'où sa place en
           fin de famille, l'ordre de l'écran. Elle se règle ici quand même, avec ce qu'on
           LIT en bas de l'écran, et non avec l'image — voir la famille voisine.

           ⚠️ DU `html` DEPUIS LE 2026-10-08 (tâche 196). Ce fut du texte simple, pour
           qu'une mise en forme ne déborde pas au bas de chaque écran ; c'est devenu un
           champ HTML à la demande, comme les autres blocs libres du site. Le bloc passe
           par `ui/Prose` (assaini), et `chassis.css` lui garde sa petite taille. Une
           valeur écrite AVANT, sans balise, garde ses retours à la ligne : voir
           `noticeHtml` dans `footer/Footer.tsx`.

           ⚠️ TOUJOURS AUCUNE `origin` : le bloc de la V1 (`FO_MEETING_MENTIONS_HTML`,
           `shared/footer.jsx`) porte sa propre couleur de fond et ses propres
           tailles, et la reprise le recopierait tel quel au bas de chaque écran.

           ⚠️ ET ELLE S'ÉTEINT AVEC LE PIED DE PAGE, comme le bandeau : le bas de
           l'écran est un seul bloc, et une mention suspendue sous rien serait
           exactement le défaut que `Footer.tsx` évite déjà pour l'image. */
        {
          key: "FO_MEETING_V2_FOOTER_NOTICE",
          needs: "FO_MEETING_V2_FOOTER_SHOW",
          section: "Mentions",
          label: "Texte",
          type: "html",
          target: "footerNotice",
          help: "Une ligne minuscule tout en bas de CHAQUE écran, SOUS l'image du bandeau, en HTML libre : mentions légales, éditeur du site, numéro de déclaration, lien vers la page légale. Vide : rien ne s'affiche.",
        },
      ],
    },
  ],

  login: [
    {
      title: "Écran de connexion",
      settings: [
        {
          key: "FO_MEETING_V2_LOGIN_TITLE",
          fallback: "Connexion",
          label: "Titre",
          type: "text",
          target: "loginTitle",
        },
        /* ⚠️ DEUX INTRODUCTIONS, QU'ON DOIT POUVOIR DÉPARTAGER (tâche 286). Les deux
           s'affichent sous le titre, et elles s'appelaient « Phrase » et « Message
           d'introduction » : rien ne disait laquelle choisir. Le sous-titre est une
           phrase en texte simple, dans le bloc du titre, comme sur les autres écrans ;
           le bloc est du HTML mis en forme, qui a sa propre ancre (`loginBlock`) parce
           qu'on règle sa place. Chaque aide nomme l'autre. Les clés restent deux :
           les fusionner serait une décision de produit. */
        {
          key: "FO_MEETING_V2_LOGIN_INTRO",
          label: "Sous-titre",
          type: "text",
          multiline: true,
          target: "loginTitle",
          help: "Une phrase en texte simple, juste sous le titre. Pour un texte mis en forme (gras, liens, images) dont on choisit la place, c'est le « Bloc d'introduction » ; les deux peuvent s'afficher ensemble. Vide : pas de sous-titre.",
        },
        {
          key: "FO_MEETING_V2_LOGIN_HTML",
          origin: {
            key: "FO_MEETING_LOGIN_MESSAGE_1",
            note: "Vérifié : `screens/Login/index.jsx` de la V1 le rend au-dessus du formulaire pendant la saisie de l'adresse (« Message au-dessus du bloc de connexion (email) »). La V1 le retire une fois l'adresse reconnue ; ici il reste, c'est le message de l'écran.",
          },
          label: "Bloc d'introduction",
          type: "html",
          target: "loginBlock",
          // Son ancien nom, et celui de la V1 : c'est celui qu'on cherchera.
          keywords: ["message"],
          help: "Un bloc mis en forme (HTML libre : gras, liens, images), sous le titre et son sous-titre, au-dessus du formulaire ou à sa gauche selon « Place du bloc ». Pour une simple phrase sous le titre, c'est le « Sous-titre ». Vide : rien ne s'affiche.",
        },
        {
          /* ⚠️ PAS DE `needs` VERS LE BLOC : `needs` ne sait juger qu'un
             INTERRUPTEUR (voir `visibleSettings`), et pointé sur une clé de texte il
             cacherait cette ligne pour toujours. Sans bloc, le choix ne commande
             rien et l'aide le dit. */
          key: "FO_MEETING_V2_LOGIN_HTML_SIDE",
          label: "Place du bloc",
          type: "select-text",
          options: [
            { value: "above", label: "Au-dessus de la saisie" },
            { value: "left", label: "À gauche de la saisie" },
          ],
          /* Vide vaut « au-dessus » : la liste le montre, sans entrée « Aucune ». */
          fallback: "above",
          target: "loginBlock",
          help: "Où se place le bloc d'introduction par rapport au formulaire. À gauche, il prend une colonne sur grand écran et repasse au-dessus sur téléphone. Sans bloc d'introduction, ce choix ne change rien. Vide : au-dessus.",
        },
        {
          key: "FO_MEETING_V2_LOGIN_EMAIL_LABEL",
          needs: "!FO_MEETING_V2_LOGIN_DISABLED",
          fallback: "Adresse email",
          label: "Libellé du champ email",
          type: "text",
          target: "loginFields",
        },
        {
          /* Une clé jamais écrite : la ligne pilote les deux clés ci-dessous. Voir le
             type `login-mode`. La V1 n'a que le lien (`FO_MEETING_AUTO_CONNECT`, vérifié :
             `screens/Login/index.jsx` remplace le mot de passe par un envoi d'e-mail,
             action 3, quand il est vrai) ; le code est sans équivalent V1 : les routes
             `otp/send` et `otp/verify` d'apix n'étaient servies qu'à l'API publique. */
          key: "FO_MEETING_V2_LOGIN_MODE",
          label: "Mode de connexion",
          type: "login-mode" as const,
          target: "loginFields",
          translatable: false,
          help: "Désactivée : personne ne peut se connecter, l'écran le dit et la barre ne propose plus le bouton. Mot de passe : la connexion classique. Lien envoyé par email : l'adresse suffit, le participant reçoit un lien qui ouvre sa session. Code envoyé par email : il reçoit un code à six chiffres et le saisit sur place, sans quitter la page ; il vaut cinq minutes et cinq essais, et une adresse jamais vue reçoit un compte à son premier code. Dans les deux derniers modes, le champ mot de passe et le lien « mot de passe oublié » disparaissent.",
        },
        {
          /* Des clés jamais écrites : ces lignes sont l'éditeur de l'email de l'action,
             pas des champs. Comme `FO_MEETING_V2_PAGES`. */
          key: "FO_MEETING_V2_LOGIN_OTP_EMAIL",
          needs: ["FO_MEETING_V2_LOGIN_OTP", "!FO_MEETING_V2_LOGIN_DISABLED"],
          label: "Email qui porte le code",
          type: "action-email" as const,
          emailAction: "otp",
          target: "loginFields",
          translatable: false,
          help: "Le même réglage que la page « Templates email » du back office, pour l'action « Code de connexion » : le compte qui envoie, et le modèle de chaque langue. Sans modèle, l'écran de connexion dira que l'événement n'envoie pas de code.",
        },
        {
          key: "FO_MEETING_V2_LOGIN_LINK_EMAIL",
          needs: [
            "FO_MEETING_V2_LOGIN_AUTO_CONNECT",
            "!FO_MEETING_V2_LOGIN_DISABLED",
          ],
          label: "Email qui porte le lien",
          type: "action-email" as const,
          emailAction: "link",
          target: "loginFields",
          translatable: false,
          help: "Le même réglage que la page « Templates email » du back office, pour l'action « Lien de connexion » : le compte qui envoie, et le modèle de chaque langue. Sans modèle, aucun lien ne part.",
        },
        {
          key: "FO_MEETING_V2_LOGIN_PASSWORD_LABEL",
          fallback: "Mot de passe",
          /* Sans mot de passe dans l'un ou l'autre mode : les deux conditions à la fois. */
          needs: [
            "!FO_MEETING_V2_LOGIN_AUTO_CONNECT",
            "!FO_MEETING_V2_LOGIN_OTP",
            "!FO_MEETING_V2_LOGIN_DISABLED",
          ],
          label: "Libellé du mot de passe",
          type: "text",
          target: "loginFields",
        },
        {
          key: "FO_MEETING_V2_LOGIN_SUBMIT",
          needs: "!FO_MEETING_V2_LOGIN_DISABLED",
          fallback: "Se connecter",
          label: "Libellé du bouton",
          type: "text",
          target: "loginButton",
        },
        {
          key: "FO_MEETING_V2_LOGIN_FORGOT_SHOW",
          /* Sans mot de passe, il n'y a rien à oublier : le lien ne s'affiche pas, et
             le proposer ici ferait régler un écran qui n'existe pas. */
          needs: [
            "!FO_MEETING_V2_LOGIN_AUTO_CONNECT",
            "!FO_MEETING_V2_LOGIN_OTP",
            "!FO_MEETING_V2_LOGIN_DISABLED",
          ],
          label: "Lien « mot de passe oublié »",
          type: "boolean",
          /* ⚠️ ALLUMÉ PAR DÉFAUT, et l'aide le disait déjà : « à retirer si… » décrit un
             réglage qu'on éteint, pas qu'on allume. Éteint, le lien n'existait sur aucun
             événement que personne n'a configuré — c'est-à-dire là où quelqu'un qui a
             perdu son mot de passe n'avait aucun recours. */
          onByDefault: true,
          target: "loginForgot",
          help: "À retirer si l'événement distribue des codes plutôt que des mots de passe : il n'y a alors rien à réinitialiser.",
        },
        {
          key: "FO_MEETING_V2_LOGIN_FORGOT_LABEL",
          fallback: "Mot de passe oublié ?",
          needs: "FO_MEETING_V2_LOGIN_FORGOT_SHOW",
          label: "Libellé du lien",
          type: "text",
          target: "loginForgot",
        },
      ],
    },
    /**
     * ═══ L'ADRESSE D'ABORD, ET CE QU'ON FAIT D'UNE ADRESSE INCONNUE ═══
     *
     * Le principe de la V1 (`screens/Login/index.jsx`), repris tel quel le 2026-09-16 :
     * l'écran vérifie l'adresse dès sa saisie, puis demande le mot de passe (ou envoie
     * le lien, ou le code) si elle est connue ; si elle ne l'est pas, il propose de
     * créer le compte, ou le refuse, selon ce groupe. Voir `auth/account.ts`.
     *
     * ⚠️ « CRÉATION DE COMPTE » EST L'INVERSE DE LA CLÉ V1. La V1 dit
     * `FO_MEETING_CREATE_ACCOUNT_HIDE` (vrai = on refuse) ; un interrupteur qui
     * s'allume pour éteindre se lit à l'envers dans le panneau, d'où le sens direct
     * ici et l'`invert` de la reprise. Allumé par défaut, comme la V1 sans réglage.
     */
    {
      title: "Adresse inconnue",
      help: "L'adresse est vérifiée dès qu'on la saisit. Reconnue : la suite dépend du mode de connexion. Inconnue : ce groupe décide s'il faut proposer de créer le compte ou refuser.",
      settings: [
        {
          key: "FO_MEETING_V2_LOGIN_SIGNUP",
          origin: {
            key: "FO_MEETING_CREATE_ACCOUNT_HIDE",
            invert: true,
            note: "Vérifié : `screens/Login/index.jsx` de la V1 propose prénom et nom à une adresse inconnue sauf si `FO_MEETING_CREATE_ACCOUNT_HIDE` est vrai (« Désactiver la création de compte » au back office). Sens inversé ici : allumé = on propose.",
          },
          needs: "!FO_MEETING_V2_LOGIN_DISABLED",
          label: "Création de compte",
          type: "boolean",
          onByDefault: true,
          target: "loginSignup",
          help: "Une adresse que le domaine ne connaît pas peut créer son compte sur place : prénom et nom, sans mot de passe, et la session s'ouvre aussitôt. Éteinte, l'écran dit que le compte n'existe pas.",
        },
        {
          key: "FO_MEETING_V2_LOGIN_SIGNUP_TYPO",
          origin: {
            key: "FO_MEETING_CREATE_ACCOUNT_TYPO",
            note: "Vérifié : `screens/Login/index.jsx` de la V1 ajoute un sélecteur de typologie au formulaire de création quand cette clé est vraie (« Activer le choix typo à la création de compte »).",
          },
          needs: ["FO_MEETING_V2_LOGIN_SIGNUP", "!FO_MEETING_V2_LOGIN_DISABLED"],
          label: "Demander la typologie à la création",
          type: "boolean",
          target: "loginSignup",
          help: "Les typologies proposées sont celles de l'étape typologie du parcours. Le choix est posé sur l'inscription dès la création du compte.",
        },
        {
          key: "FO_MEETING_V2_LOGIN_UNKNOWN_HTML",
          origin: {
            key: "FO_MEETING_LOGIN_MESSAGE_3",
            note: "Vérifié : `screens/Login/index.jsx` de la V1 le rend au-dessus du formulaire de création, et à la place du formulaire quand la création est désactivée (« Message au-dessus du bloc de connexion si email inconnu (création) »).",
          },
          needs: "!FO_MEETING_V2_LOGIN_DISABLED",
          label: "Message pour une adresse inconnue",
          type: "html",
          target: "loginSignup",
          help: "Au-dessus des champs de création du compte ; ou, création éteinte, à la place du formulaire. Vide : rien au-dessus des champs, et « Ce compte n'existe pas. » en cas de refus.",
        },
        {
          key: "FO_MEETING_V2_LOGIN_KNOWN_HTML",
          origin: {
            key: "FO_MEETING_LOGIN_MESSAGE_2",
            note: "Vérifié : `screens/Login/index.jsx` de la V1 le rend au-dessus du formulaire une fois l'adresse reconnue (« Message au-dessus du bloc de connexion si email connu (connexion) »).",
          },
          needs: "!FO_MEETING_V2_LOGIN_DISABLED",
          label: "Message pour une adresse reconnue",
          type: "html",
          target: "loginFields",
          help: "Au-dessus du mot de passe, ou de l'envoi du lien ou du code, une fois l'adresse reconnue. Vide : rien ne s'affiche.",
        },
      ],
    },
  ],

  /**
   * L'INSCRIPTION, ÉTAPE 1 : comment on participe.
   *
   * Une question, quatre réponses possibles, et chaque réponse est un réglage à
   * deux faces : elle s'affiche ou non, et son libellé s'écrit. Un événement
   * sans distanciel retire la ligne ; un événement qui ne veut pas du mot
   * « distanciel » la renomme.
   *
   * ⚠️ LES INTERRUPTEURS SONT ALLUMÉS PAR DÉFAUT (`onByDefault`). Un réglage
   * absent vaut `false`, donc sur un événement que personne n'a configuré la
   * question n'aurait aucune réponse à proposer. La V1 contourne ça en nommant
   * ses réglages `…_HIDE_…`, ce qui revient à éteindre quand on croit allumer :
   * ici le sens reste positif et c'est la valeur par défaut qui bouge.
   */
  registration: [
    {
      needs: "FO_MEETING_V2_STEP_REGISTRATION_SHOW",
      title: "Étape 1 · participation",
      help: "La première question posée à un participant. Ce qu'il répond ici décide de la suite de son inscription.",
      settings: [
        {
          key: "FO_MEETING_V2_REGISTRATION_TITLE",
          label: "Titre",
          type: "text",
          fallback: "Votre participation",
          target: "registrationTitle",
        },
        {
          key: "FO_MEETING_V2_REGISTRATION_INTRO",
          label: "Sous-titre",
          type: "text",
          multiline: true,
          fallback: "Indiquez comment vous rejoindrez l'événement.",
          target: "registrationTitle",
        },
        {
          key: "FO_MEETING_V2_REGISTRATION_HTML",
          label: "Message d'introduction",
          type: "html",
          target: "registrationTitle",
          help: "Affiché au-dessus des réponses, en HTML libre. Vide : rien ne s'affiche.",
        },
        {
          key: "FO_MEETING_V2_REGISTRATION_SUBMIT",
          label: "Libellé du bouton",
          type: "text",
          fallback: "Continuer",
          target: "registrationButton",
        },
      ],
    },
    {
      needs: "FO_MEETING_V2_STEP_REGISTRATION_SHOW",
      title: "Réponses proposées",
      help: "Retirez celles qui n'ont pas de sens pour cet événement. Une question sans réponse possible ne s'affiche pas du tout.",
      settings: [
        {
          key: "FO_MEETING_V2_REGISTRATION_ONSITE_SHOW",
          label: "Présentiel",
          type: "boolean",
          onByDefault: true,
          target: "registrationOptions",
        },
        {
          key: "FO_MEETING_V2_REGISTRATION_ONSITE_LABEL",
          needs: "FO_MEETING_V2_REGISTRATION_ONSITE_SHOW",
          label: "Présentiel · libellé",
          type: "text",
          fallback: "Je serai présent sur place",
          target: "registrationOptions",
        },
        {
          key: "FO_MEETING_V2_REGISTRATION_REMOTE_SHOW",
          origin: {
            key: "FO_MEETING_PARTICIPATON_HIDE_IS_REMOTE",
            invert: true,
            note: "Vérifié : `Participation.jsx` ajoute l'option quand la clé est FAUSSE, d'où l'inversion. (La faute de frappe « PARTICIPATON » est celle de la V1.)",
          },
          label: "Distanciel",
          type: "boolean",
          onByDefault: true,
          target: "registrationOptions",
        },
        {
          key: "FO_MEETING_V2_REGISTRATION_REMOTE_LABEL",
          needs: "FO_MEETING_V2_REGISTRATION_REMOTE_SHOW",
          label: "Distanciel · libellé",
          type: "text",
          fallback: "Je participerai à distance",
          target: "registrationOptions",
        },
        {
          key: "FO_MEETING_V2_REGISTRATION_REPRESENTED_SHOW",
          origin: {
            key: "FO_MEETING_SHOW_REPRESENTED_BY",
            note: "La V1 porte aussi `..._HTML` (le texte de l'écran) ; c'est bien le booléen qui offre l'option (`screens/steps/RepresentedBy.jsx`).",
          },
          label: "Représentation",
          type: "boolean",
          target: "registrationOptions",
          help: "Pour les événements où un invité peut envoyer quelqu'un à sa place.",
        },
        {
          key: "FO_MEETING_V2_REGISTRATION_REPRESENTED_LABEL",
          needs: "FO_MEETING_V2_REGISTRATION_REPRESENTED_SHOW",
          label: "Représentation · libellé",
          type: "text",
          fallback: "Je serai représenté",
          target: "registrationOptions",
        },
        {
          key: "FO_MEETING_V2_REGISTRATION_DECLINE_SHOW",
          label: "Refus",
          type: "boolean",
          onByDefault: true,
          target: "registrationOptions",
          help: "Retirer cette réponse ne fait pas venir les gens : elle laisse simplement l'organisateur sans réponse de leur part.",
        },
        {
          key: "FO_MEETING_V2_REGISTRATION_DECLINE_LABEL",
          needs: "FO_MEETING_V2_REGISTRATION_DECLINE_SHOW",
          label: "Refus · libellé",
          type: "text",
          fallback: "Je ne participe pas",
          target: "registrationOptions",
        },
        {
          key: "FO_MEETING_V2_REGISTRATION_REASON_ASK",
          needs: "FO_MEETING_V2_REGISTRATION_DECLINE_SHOW",
          label: "Demander la raison de l'absence",
          type: "boolean",
          target: "registrationOptions",
        },
        {
          key: "FO_MEETING_V2_REGISTRATION_REASON_LABEL",
          needs: "FO_MEETING_V2_REGISTRATION_REASON_ASK",
          label: "Raison · libellé de la question",
          type: "text",
          fallback: "Pouvez-vous nous dire pourquoi ?",
          target: "registrationOptions",
        },
        {
          key: "FO_MEETING_V2_REGISTRATION_REASON_REQUIRED",
          needs: "FO_MEETING_V2_REGISTRATION_REASON_ASK",
          label: "Raison · réponse obligatoire",
          type: "boolean",
          target: "registrationOptions",
        },
      ],
    },
    {
      needs: "FO_MEETING_V2_STEP_REGISTRATION_SHOW",
      title: "Automatismes",
      help: "Le réglage qui retire l'étape du parcours, parce qu'il n'y a plus rien à demander. Les heures d'ouverture et de clôture, elles, verrouillent le parcours ENTIER : elles se règlent dans « Le site ».",
      settings: [
        {
          key: "FO_MEETING_V2_REGISTRATION_AUTO",
          label: "Inscription automatique",
          type: "boolean",
          target: "registrationOptions",
          help: "La question n'est pas posée : chacun est inscrit en présentiel dès sa connexion, et le parcours commence à l'étape suivante.",
        },
      ],
    },
  ],

  /**
   * LA TYPOLOGIE — le profil du participant, et le pivot de toute la cascade.
   *
   * ⚠️ CE QUE LE PARTICIPANT CHOISIT ICI CHANGE LA CONFIGURATION QU'IL LIT. C'est
   * le vrai mécanisme du produit : son `typoId` prime sur la langue dans la
   * cascade, donc répondre « Exposant » ne change pas seulement une étiquette —
   * tout le reste du site bascule sur les surcharges de cette typologie.
   *
   * ⚠️ LES INTERRUPTEURS SONT ENGENDRÉS, un par typologie de l'événement. Leurs
   * clés portent un identifiant que seule la base connaît : une liste écrite ici
   * ne pourrait qu'en inventer trois, ce que fait le prototype faute de base
   * derrière lui.
   */
  typology: [
    {
      needs: "FO_MEETING_V2_STEP_TYPOLOGY_SHOW",
      title: "Étape · typologie",
      settings: [
        {
          key: "FO_MEETING_V2_TYPO_TITLE",
          label: "Titre",
          type: "text",
          fallback: "Vous êtes",
          target: "typoTitle",
        },
        {
          key: "FO_MEETING_V2_TYPO_SUBTITLE",
          label: "Sous-titre",
          type: "text",
          multiline: true,
          fallback: "Votre profil détermine votre parcours et vos accès.",
          target: "typoTitle",
        },
        {
          key: "FO_MEETING_V2_TYPO_HTML",
          origin: {
            key: "FO_MEETING_TYPO_HTML",
            note: 'Vérifié : `screens/steps/Typos.jsx` de la V1 le rend en tête de l\'écran (`<HTML name="FO_MEETING_TYPO_HTML" />`).',
          },
          label: "Message d'introduction",
          type: "html",
          target: "typoTitle",
          help: "Affiché sous le titre, avant les typologies proposées, en HTML libre. Vide : rien ne s'affiche.",
        },
      ],
    },
    {
      needs: "FO_MEETING_V2_STEP_TYPOLOGY_SHOW",
      title: "Typologies proposées",
      help: "Une typologie retirée d'ici reste atteignable par son lien d'invitation : c'est le circuit des exposants et de la presse. Elle disparaît du choix, pas de l'événement.",
      /**
       * ═══ DEUX LIGNES PAR TYPOLOGIE : EST-ELLE PROPOSÉE, ET COMMENT S'EXPLIQUE-T-ELLE ═══
       *
       * ⚠️ LA PRÉCISION EST UN RÉGLAGE PARCE QUE LE PRODUIT N'EN A PAS. La table `Typos`
       * ne porte QU'UN LIBELLÉ — ni description, ni icône, ni couleur. « MARQUES »,
       * « DETAILLANT », « TOURISME » : les noms d'un vrai événement sont des mots de
       * métier, et personne d'extérieur ne sait lequel le concerne. La phrase manquait
       * donc au participant, et le seul endroit où l'organisateur peut l'écrire est
       * ici.
       *
       * ⚠️ ELLE SUIT LA CASCADE, comme tout le reste : traduisible par langue, et
       * surchargeable par typologie de lecture. C'est ce qui la distingue d'une colonne
       * qu'on aurait ajoutée en base.
       *
       * ⚠️ `section`, PARCE QUE CES RÉGLAGES VONT PAR PAIRES. À plat, six typologies
       * donnent douze lignes dont l'appariement ne tiendrait qu'à un suffixe répété sur
       * une ligne sur deux. Le nom de la typologie se dit une fois, et ses deux lignes
       * s'appellent enfin par ce qu'elles sont.
       */
      settings: ({ typos }) =>
        typos.flatMap((typo) => [
          {
            key: `FO_MEETING_V2_TYPO_SHOW_${typo.id}`,
            label: "Proposée",
            section: typo.name,
            type: "boolean" as const,
            // Proposée tant que personne ne l'a retirée — comme le prototype, qui
            // ne masque que sur un `false` explicite.
            onByDefault: true,
            target: "typoOptions",
          },
          {
            key: `FO_MEETING_V2_TYPO_NOTE_${typo.id}`,
            label: "Précision",
            section: typo.name,
            type: "text" as const,
            // Sans elle, la ligne ne porte que le nom : c'est l'état d'aujourd'hui, et
            // il reste parfaitement valable pour « Étudiant » ou « Visiteur ».
            needs: `FO_MEETING_V2_TYPO_SHOW_${typo.id}`,
            target: "typoOptions",
            help: "La phrase sous le nom, dans le choix du participant. Vide : rien ne s'affiche.",
          },
        ]),
    },
  ],

  /**
   * L'ÉTAPE DATES — quels jours, sur un événement qui en a plusieurs.
   *
   * ⚠️ ELLE DISPARAÎT D'ELLE-MÊME SUR UN ÉVÉNEMENT D'UN SEUL JOUR, et c'est le cas le
   * plus courant : ce n'est pas l'interrupteur qui la retire presque partout, c'est
   * l'absence de sujet (voir `funnel/thread.ts`). L'interrupteur, lui, sert à la
   * retirer d'un événement qui DURE plusieurs jours mais n'en fait pas choisir — un
   * abonnement, un pass unique.
   *
   * ⚠️ ET LA RETIRER N'A JAMAIS VOULU DIRE « AUCUN JOUR » : sans question posée, le
   * participant est inscrit à TOUS les jours. C'est ce que fait la V1, et c'est ce que
   * la confirmation écrit — voir `funnel/dates.ts`. L'aide le dit, parce que
   * l'intuition dit le contraire.
   */
  dates: [
    {
      needs: "FO_MEETING_V2_STEP_DATES_SHOW",
      title: "Étape · dates",
      help: "Ne s'affiche que si l'événement compte plusieurs jours. Retirée, elle n'annule rien : chacun est inscrit à tous les jours.",
      settings: [
        {
          key: "FO_MEETING_V2_DATES_TITLE",
          origin: { key: "FO_MEETING_DATES_TITLE" },
          label: "Titre",
          type: "text",
          fallback: "Vos dates de présence",
          target: "datesTitle",
        },
        {
          key: "FO_MEETING_V2_DATES_SUBTITLE",
          label: "Sous-titre",
          type: "text",
          multiline: true,
          fallback: "Cochez les jours où vous serez là.",
          target: "datesTitle",
        },
        {
          key: "FO_MEETING_V2_DATES_HTML",
          origin: {
            key: "FO_MEETING_DATES_HTML",
            note: 'Vérifié : `screens/steps/Dates.jsx` de la V1 le rend en tête de l\'écran (`<HTML name="FO_MEETING_DATES_HTML" />`).',
          },
          label: "Message d'introduction",
          type: "html",
          target: "datesTitle",
          help: "Affiché au-dessus des jours, en HTML libre. Vide : rien ne s'affiche.",
        },
      ],
    },
  ],

  /**
   * L'ÉTAPE INFORMATIONS — un formulaire du BACK OFFICE, pas un constructeur.
   *
   * ⚠️ ELLE NE CONSTRUIT PAS LE FORMULAIRE, ELLE EN DÉSIGNE UN. Le prototype invente
   * un constructeur de champs faute de base derrière lui ; le produit garde ses
   * formulaires dans le back office, et c'est un réglage qui nomme celui que cette
   * étape sert. Reproduire le constructeur aurait donné une seconde définition de
   * chaque champ, qui aurait divergé de la première.
   */
  informations: [
    {
      /**
       * ⚠️ L'INTERRUPTEUR DE CETTE INSTANCE, pas celui de la première : elles partagent
       * cette famille, et une clé écrite en dur faisait décider la première du sort de
       * la seconde.
       */
      needs: (instance) => stepShowKey(INFORMATIONS_SCREEN, instance),
      title: "Étape · informations",
      help: "Le formulaire vient du back office. Cette étape choisit lequel servir et ce qui s'écrit autour.",
      /**
       * ═══ TOUT EST PAR INSTANCE ═══
       *
       * ⚠️ L'ÉCRAN LISAIT DÉJÀ DES CLÉS SUFFIXÉES (`Informations.tsx`), LE PANNEAU NON :
       * l'organisateur choisissait un formulaire pour la seconde étape et il partait dans
       * la clé de la PREMIÈRE. Les deux étapes servaient donc le même formulaire, et
       * celle qu'on venait d'ajouter n'en avait aucun. C'est ce qui a été signalé.
       *
       * La première n'est pas suffixée : `instanceKey` le garantit, et c'est ce qui rend
       * à un événement déjà réglé ses valeurs.
       */
      settings: ({ forms, instance }) => [
        {
          key: instanceKey("FO_MEETING_V2_INFO_FORM_ID", instance),
          /* La V1 n'a qu'une étape « informations » : seule la première a une origine à
             reprendre. En donner une à la seconde ferait proposer la même valeur deux
             fois, et la reprise écraserait le choix qu'on vient de faire. */
          ...(instance
            ? {}
            : {
                origin: {
                  key: "FO_MEETING_INFORMATIONS_FORM_ID",
                  note: "Le back office l'appelle « Informations ».",
                },
              }),
          label: "Formulaire servi",
          type: "select-forms-user" as const,
          target: "infoForm",
          /**
           * ⚠️ LES FORMULAIRES DE PARTICIPANT SEULEMENT — c'est ce que dit le type du
           * réglage, et l'événement de test en offrait 15 dont 6 ne le sont pas : deux
           * formulaires de société, et quatre lignes héritées à un seul champ sans
           * modèle, que le rendu écarte, donc une étape vide pour le visiteur.
           */
          options: formsOf(forms, "U"),
          help: "Ses questions se créent dans le back office ; leur présentation dans ce parcours se règle juste en dessous.",
        },
        {
          /**
           * ⚠️ UNE CLÉ QUI N'EST JAMAIS ÉCRITE. Comme `FO_MEETING_V2_PAGES`, ce réglage
           * ne porte pas de valeur : il donne une place dans le panneau à une interface
           * qui écrit ailleurs — dans les `FormModels` du back office, par sa propre
           * route. La clé sert d'identité de ligne (repli, recherche, ancre), pas de
           * rangement.
           *
           * Pas d'`origin` : la V1 n'a rien d'équivalent, elle envoie au back office.
           */
          key: instanceKey("FO_MEETING_V2_INFO_FIELDS", instance),
          label: "Présentation des champs",
          type: "form-editor" as const,
          // QUEL formulaire cet éditeur ouvre : celui que la ligne du dessus désigne.
          // Déclaré, parce que l'étape société a le sien et que l'éditeur ne peut plus
          // deviner lequel des deux il sert.
          formKey: instanceKey("FO_MEETING_V2_INFO_FORM_ID", instance),
          target: "infoForm",
          translatable: false,
          help: "Le type, la largeur et l'ordre des champs, dans ce formulaire. Un champ « pleine largeur » occupe sa ligne ; les autres se rangent côte à côte tant qu'ils tiennent. Les questions elles-mêmes restent au back office : les renommer ici les renommerait pour tous les formulaires du domaine.",
        },
        {
          key: instanceKey("FO_MEETING_V2_INFO_TITLE", instance),
          label: "Titre",
          type: "text" as const,
          fallback: "Vos informations",
          target: "infoTitle",
        },
        {
          key: instanceKey("FO_MEETING_V2_INFO_SUBTITLE", instance),
          label: "Sous-titre",
          type: "text" as const,
          multiline: true,
          fallback:
            "Elles figureront sur votre badge et serviront à vous joindre.",
          target: "infoTitle",
        },
        {
          key: instanceKey("FO_MEETING_V2_INFO_HTML", instance),
          label: "Message d'introduction",
          type: "html" as const,
          target: "infoTitle",
          help: "Affiché au-dessus du formulaire, en HTML libre. Vide : rien ne s'affiche.",
        },
      ],
    },
  ],

  /**
   * L'ÉTAPE SOCIÉTÉ — la personne, puis la maison pour laquelle elle vient.
   *
   * ⚠️ CETTE ÉTAPE S'AJOUTE, elle ne se retire pas : elle est absente tant que
   * l'organisateur ne la demande pas (voir `optIn` dans le registre). Un salon
   * professionnel la veut, une soirée client n'en a que faire — et réclamer un SIRET
   * à qui n'en a pas est un cul-de-sac, pas un champ vide.
   *
   * ⚠️ UNE SOCIÉTÉ N'APPARTIENT PAS À L'ÉVÉNEMENT, MAIS AU DOMAINE. Deux participants
   * du même salon qui saisissent le même identifiant doivent tomber sur la MÊME fiche
   * — c'est tout l'objet de l'écran d'identifiant, et c'est aussi pourquoi le serveur
   * refuse un identifiant déjà pris (`SIRET_ALREADY_IN_USE_MESSAGE`).
   */
  company: [
    {
      needs: "FO_MEETING_V2_STEP_COMPANY_SHOW",
      title: "Étape · société",
      help: "Le participant se rattache à une société du domaine, ou la crée. Le formulaire de la fiche vient du back office ; cette étape choisit lequel servir.",
      settings: ({ forms }) => [
        {
          key: "FO_MEETING_V2_COMPANY_FORM_ID",
          origin: {
            key: "FO_MEETING_CREATE_COMPANY_FORM_ID",
            note: "Vérifié : le back office l'intitule « Créer une société » et n'y propose que les formulaires de type `C` (`bo/src/pages/settings/meeting/Forms.jsx`) ; la V1 le sert dans `screens/companies/CompanyCreate.jsx`.",
          },
          label: "Formulaire de la fiche",
          type: "select-forms-company" as const,
          target: "companyForm",
          /**
           * ⚠️ LES FORMULAIRES DE SOCIÉTÉ SEULEMENT, comme le back office. Servir ici
           * un formulaire de participant écrirait le nom et l'email du visiteur dans
           * l'enregistrement de sa société — `saveAnswers` range chaque champ sous SON
           * modèle, et un formulaire `U` n'a rien à ranger sous `C`.
           */
          options: formsOf(forms, "C"),
          help: "Sans lui, l'étape peut rattacher à une société existante mais pas en créer une : il n'y aurait rien à remplir.",
        },
        {
          // Même nature que `FO_MEETING_V2_INFO_FIELDS` : une clé qui n'est jamais
          // écrite, elle ne fait qu'ouvrir l'éditeur sur le bon formulaire.
          key: "FO_MEETING_V2_COMPANY_FIELDS",
          label: "Présentation des champs",
          type: "form-editor" as const,
          formKey: "FO_MEETING_V2_COMPANY_FORM_ID",
          target: "companyForm",
          translatable: false,
          help: "Le type, la largeur et l'ordre des champs de la fiche société. Les questions elles-mêmes restent au back office.",
        },
        {
          key: "FO_MEETING_V2_COMPANY_TITLE",
          label: "Titre",
          type: "text" as const,
          fallback: "Votre société",
          target: "companyTitle",
        },
        {
          key: "FO_MEETING_V2_COMPANY_SUBTITLE",
          label: "Sous-titre",
          type: "text" as const,
          multiline: true,
          fallback: "Rattachez-vous à la société que vous représentez.",
          target: "companyTitle",
        },
        {
          key: "FO_MEETING_V2_COMPANY_HTML",
          origin: {
            key: "FO_MEETING_COMPANIES_HTML",
            note: 'Vérifié : `screens/companies/CompaniesList.jsx` de la V1 le rend en tête de la liste (`<HTML name="FO_MEETING_COMPANIES_HTML" />`).',
          },
          label: "Message d'introduction",
          type: "html" as const,
          target: "companyTitle",
          help: "Affiché au-dessus de la liste, en HTML libre. Vide : rien ne s'affiche.",
        },
        {
          key: "FO_MEETING_V2_COMPANY_INDEX_LABEL",
          origin: {
            key: "COMPANY_INDEX_LABEL",
            note: "Vérifié : `hooks/useConf.js` de la V1 l'expose en `companyIndexLabel`, lu par `CompanySiren.jsx` (l'intitulé du champ) et par `CompaniesList.jsx` (l'en-tête de colonne).",
          },
          section: "L'identifiant",
          label: "Nom de l'identifiant",
          type: "text" as const,
          fallback: "SIREN de la société",
          target: "companyIndex",
          help: "Ce qui identifie une société de façon unique dans votre domaine. SIREN en France, autre chose ailleurs, d'où le fait qu'il se nomme.",
        },
        {
          key: "FO_MEETING_V2_COMPANY_INDEX_SIZE",
          origin: {
            key: "COMPANY_INDEX_MAX_SIZE",
            note: "Vérifié : `hooks/useConf.js` de la V1 l'expose en `companyIndexSize`, et `CompanySiren.jsx` en fait à la fois la longueur EXIGÉE et le `maxLength` du champ, avec 9 à défaut.",
          },
          section: "L'identifiant",
          label: "Nombre de chiffres",
          type: "number" as const,
          fallback: "9",
          target: "companyIndex",
          help: "L'identifiant doit en compter exactement autant, et n'accepte que des chiffres. Vide : 9, la longueur d'un SIREN.",
        },
        {
          key: "FO_MEETING_V2_COMPANY_INDEX_INTER",
          origin: {
            key: "FO_INDEX_INTER",
            note: "Vérifié : `CompanySiren.jsx` de la V1 bascule dessus vers le choix du pays et les formats de `screens/companies/indexes.js`.",
          },
          section: "L'identifiant",
          label: "Identifiants étrangers",
          type: "boolean" as const,
          target: "companyIndex",
          help: "Le participant choisit d'abord son pays, et le format attendu suit : SIRET, Numero REA, UID suisse, NIF. « Nombre de chiffres » ne s'applique plus.",
        },
        {
          key: "FO_MEETING_V2_COMPANY_INDEX_HTML",
          origin: {
            key: "FO_MEETING_COMPANIES_INDEX_HTML",
            note: "Vérifié : `useConf.js` l'expose en `COMPANIES_INDEX_HTML`, rendu par `CompanySiren.jsx` au-dessus du champ.",
          },
          section: "L'identifiant",
          label: "Message au-dessus du champ",
          type: "html" as const,
          target: "companyIndex",
          help: "Où trouver ce numéro, par exemple. Vide : rien ne s'affiche.",
        },
      ],
    },
  ],

  /**
   * L'ÉTAPE CONFÉRENCES — le programme, et un créneau par conférence.
   *
   * ⚠️ TRÈS PEU DE RÉGLAGES, ET C'EST LE SUJET. Tout ce qui gouverne cette étape est
   * DÉJÀ ailleurs : les conférences et leurs créneaux au back office, les quotas et le
   * chevauchement dans la route qui les rend, « un seul collaborateur par société »
   * dans un réglage que le SERVEUR lit sous son nom V1 et qu'on ne peut donc pas
   * re-clé. Ce qui reste ici est ce que le site DIT autour. Ajouter une ligne de plus
   * — un maximum, par exemple — promettrait une règle que rien n'applique : voir
   * `funnel/conferences.ts`.
   */
  /**
   * L'ÉTAPE BILLETTERIE — ce qui se vend, et ce que ça coûte.
   *
   * ⚠️ CE GROUPE NE PORTE AUCUN PRIX, ET C'EST LE POINT. Les articles, leurs tarifs,
   * leur TVA, les jours où chacun s'ouvre à la vente, les gratuits et les codes de
   * remise se règlent au BACK OFFICE, dans une table à eux — la V2 n'a rien à y dire.
   * Le prototype, lui, offre « prix par jour » et « prix par accompagnant » : deux
   * réglages qui n'existent nulle part dans le produit, et qui auraient affiché des
   * montants que la facture aurait ensuite démentis. Ce qui se règle ici, c'est ce qui
   * s'ÉCRIT autour de la liste.
   *
   * ⚠️ ET PAS D'INTERRUPTEUR DU CODE PROMO. Les codes se créent au back office
   * (`ArticleCodes`, avec un contingent qui se partage entre collaborateurs d'une même
   * société) ; le champ où le participant tape le sien (`funnel/PromoCode.tsx`, #362)
   * suit le paiement, comme le faisait la V1 : il paraît sous le solde dès qu'il y a
   * quelque chose à payer, et disparaît avec `FO_MEETING_V2_PAY_SHOW`. Un événement
   * sans code n'a qu'à ne pas en créer : le lien ne mène alors qu'à « ce code n'existe
   * pas ». Le prototype, lui, simule le sien (« GALA10 ») faute de base derrière.
   */
  articles: [
    {
      needs: "FO_MEETING_V2_STEP_ARTICLES_SHOW",
      title: "Étape · billetterie",
      help: "Le catalogue vient du back office : les articles, leurs prix, leur TVA et les jours où ils se vendent s'y règlent. Cette étape ne décide que de ce qui s'écrit autour, et de la façon dont on choisit.",
      settings: [
        {
          key: "FO_MEETING_V2_ARTICLES_TITLE",
          label: "Titre",
          type: "text",
          fallback: "Votre billet",
          target: "articlesTitle",
        },
        {
          key: "FO_MEETING_V2_ARTICLES_SUBTITLE",
          label: "Sous-titre",
          type: "text",
          multiline: true,
          fallback:
            "Choisissez ce que vous souhaitez ajouter à votre inscription.",
          target: "articlesTitle",
        },
        {
          key: "FO_MEETING_V2_ARTICLES_HTML",
          origin: {
            key: "FO_MEETING_ARTICLES_HTML",
            note: "Vérifié : `screens/steps/Articles.jsx` de la V1 le rend en tête de l'écran (`<HTML name/>`).",
          },
          label: "Message d'introduction",
          type: "html",
          target: "articlesTitle",
          help: "Affiché au-dessus de la liste, en HTML libre. C'est l'endroit des conditions de vente et des dates de tarif préférentiel. Vide : rien ne s'affiche.",
        },
        {
          key: "FO_MEETING_V2_ARTICLES_REQUIRED",
          origin: {
            key: "FO_MEETING_ARTICLES_REQUIRED",
            note: "Vérifié : `screens/steps/Articles.jsx` de la V1 en fait la règle `required` de chaque `Form.Item`, donc UNE PAR JOUR proposé, et non une sur l'ensemble.",
          },
          label: "Un choix obligatoire par journée",
          type: "boolean",
          target: "articlesTitle",
          help: "Exige au moins un article pour CHAQUE journée proposée, celle du niveau « hors jour » comprise. Laissé libre, on peut traverser l'étape sans rien prendre.",
        },
        {
          key: "FO_MEETING_V2_ARTICLES_NOTICE",
          label: "Mention sous le récapitulatif",
          type: "text",
          multiline: true,
          target: "articlesNotice",
          help: "Une ligne discrète sous le total : conditions d'annulation, mention de paiement sécurisé. Vide : rien ne s'affiche.",
        },
      ],
    },
  ],

  /**
   * L'ÉTAPE ACCOMPAGNANTS — et le parcours qu'elle ouvre.
   *
   * ⚠️ DEUX FAMILLES, PARCE QUE CE SONT DEUX CHOSES. Ce que l'étape dit au porteur
   * (« qui vient avec vous ? », combien il peut en inscrire) n'est pas ce qu'un
   * ACCOMPAGNANT traverse. Le second parcours est décorrélé du premier : ses étapes ont
   * leurs propres interrupteurs, et son ordre est celui de la V1 — le formulaire
   * d'abord, puisque c'est lui qui crée la personne dont les étapes suivantes ont besoin.
   *
   * ⚠️ CE QUI NE S'Y RÈGLE PAS : ni participation (on n'inscrit pas quelqu'un qui
   * décline), ni typologie (elle est HÉRITÉE du porteur — la V1 recopie ses `typo` et
   * `typoId`), ni société (sauf pour un collaborateur : voir « Proposer ses
   * collaborateurs »), ni confirmation. Voir `funnel/companionThread.ts`.
   */
  companions: [
    {
      needs: "FO_MEETING_V2_STEP_COMPANIONS_SHOW",
      title: "Étape · accompagnants",
      help: "Ce que le porteur voit : qui il a déjà inscrit, et de quoi en ajouter.",
      settings: [
        {
          key: "FO_MEETING_V2_COMPANIONS_TITLE",
          label: "Titre",
          type: "text",
          fallback: "Vos accompagnants",
          target: "companionsTitle",
        },
        {
          key: "FO_MEETING_V2_COMPANIONS_SUBTITLE",
          label: "Sous-titre",
          type: "text",
          multiline: true,
          fallback: "Chacun recevra son propre badge.",
          target: "companionsTitle",
        },
        {
          key: "FO_MEETING_V2_COMPANIONS_HTML",
          label: "Message d'introduction",
          type: "html",
          target: "companionsTitle",
          help: "Affiché sous le titre, avant la liste des accompagnants, en HTML libre. Vide : rien ne s'affiche.",
        },
        {
          key: "FO_MEETING_V2_COMPANIONS_MAX",
          origin: {
            key: "FO_MEETING_COMPANIONS_MAX",
            note: "Vérifié : la V1 la lit pour plafonner le nombre d'accompagnants qu'un participant peut inscrire.",
          },
          label: "Nombre maximum",
          type: "number",
          target: "companionsTitle",
          help: "Vide ou zéro : aucun plafond. Atteint, le bouton d'ajout disparaît et l'étape dit pourquoi.",
        },
        {
          key: "FO_MEETING_V2_COMPANIONS_COWORKERS",
          origin: {
            key: "FO_MEETING_COMPANIONS_ASSOCIATES_IS_ACTIVE",
            note: "Vérifié : `screens/steps/Confirmation/index.jsx` de la V1 ouvre la liste des collaborateurs (`addAssociates.jsx`, route apix `users/associates/me`) au lieu du formulaire quand elle est vraie.",
          },
          label: "Proposer ses collaborateurs",
          type: "boolean",
          target: "companionsTitle",
          help: "Ajoute un bouton qui ouvre, dans une fenêtre avec une recherche, les personnes des sociétés du participant : il en choisit une pour l'inscrire comme accompagnant, sans ressaisir son nom. Les personnes déjà inscrites y restent, sans pouvoir être choisies. L'ajout à la main reste possible. Chaque personne inscrite ainsi est rattachée à une société du participant : d'office s'il n'en a qu'une ; s'il en a plusieurs, une étape « Société » ouvre son parcours.",
        },
        {
          /* V2 only: the V1 never attached a companion to a company. Asked by William on
             2026-10-28, a setting so as to stay flexible. With coworkers offered it is
             always the case: they are coworkers. See `routes/Companions.tsx`. */
          key: "FO_MEETING_V2_COMPANIONS_COMPANY",
          label: "Rattacher les accompagnants à une société",
          type: "boolean",
          target: "companionsTitle",
          help: "Chaque personne que le participant inscrit est rattachée à l'une de ses sociétés : d'office s'il n'en a qu'une ; s'il en a plusieurs, une étape « Société » ouvre son parcours. Déjà le cas quand vous proposez ses collaborateurs.",
        },
        {
          key: "FO_MEETING_V2_EMAIL_COMPANION_CREATED",
          label: "Email à l'accompagnant créé",
          type: "action-email" as const,
          emailAction: "companionCreated",
          target: "companionsTitle",
          translatable: false,
          help: "Part dès qu'un accompagnant est ajouté AVEC une adresse email (la V1 l'envoie après l'invitation). Le même réglage que la page « Templates email » du back office : le compte qui envoie, et le modèle de chaque langue. Sans modèle, rien ne part et l'inscription se passe de lui.",
        },
      ],
    },
    {
      needs: "FO_MEETING_V2_STEP_COMPANIONS_SHOW",
      title: "Parcours d'un accompagnant",
      help: "Ce qu'on remplit POUR LUI, et dans cet ordre. Le formulaire vient en premier : c'est lui qui crée la personne, et les étapes suivantes ont besoin de son identifiant pour écrire quoi que ce soit. Seule exception, quand les accompagnants sont rattachés à une société (collaborateurs proposés, ou « Rattacher les accompagnants à une société ») et que le participant en a plusieurs : une étape « Société » passe devant, et le formulaire crée la personne déjà rattachée à celle choisie.",
      /* ⚠️ ENGENDRÉ, PARCE QU'IL FAUT LA LISTE DES FORMULAIRES. Déclaré en dur, ce groupe
         servait un sélecteur SANS options : la ligne affichait « cet événement n'en
         propose aucun » sur un événement qui en porte quinze, et le formulaire d'un
         accompagnant ne pouvait donc pas se choisir du tout. */
      settings: ({ forms }) => [
        {
          key: "FO_MEETING_V2_COMPANION_FORM",
          origin: {
            key: "FO_MEETING_COMPANION_FORM_ID",
            note: "Vérifié : `hooks/useConf.js` de la V1 rend `FO_MEETING_COMPANION_FORM_ID || settings.formId` dès que la participation vaut 5.",
          },
          label: "Son formulaire",
          type: "select-forms-user" as const,
          options: formsOf(forms, "U"),
          target: "companionForm",
          help: "Un accompagnant n'a pas à remplir tout ce qu'on demande au porteur. Vide : le formulaire de l'événement.",
        },
        {
          /**
           * ⚠️ ELLE MANQUAIT, ET SON ABSENCE NE SE VOYAIT QUE DEPUIS LA PAGE. L'étape
           * informations et la fiche société ouvrent chacune l'éditeur sur le formulaire
           * qu'elles servent ; le parcours de l'accompagnant, non — on pouvait CHOISIR
           * son formulaire sans jamais pouvoir en ranger les champs, alors que c'est le
           * même éditeur, le même enregistrement et le même back office derrière.
           *
           * Même nature que `FO_MEETING_V2_INFO_FIELDS` : une clé qui n'est JAMAIS
           * écrite, elle ne fait qu'ouvrir l'éditeur sur le formulaire que la ligne du
           * dessus désigne. Pas d'`origin` : la V1 n'a rien d'équivalent.
           */
          key: "FO_MEETING_V2_COMPANION_FIELDS",
          label: "Présentation des champs",
          type: "form-editor" as const,
          // QUEL formulaire cet éditeur ouvre : celui de l'ACCOMPAGNANT, et lui seul.
          // ⚠️ PAS DE REPLI SUR CELUI DE L'ÉVÉNEMENT, à la différence de l'écran
          // (`useChosenForm` en a un) : ici on ÉCRIT. Replié, ranger « les champs de
          // l'accompagnant » aurait réécrit le formulaire du PORTEUR — le même que sert
          // l'étape informations — sans que rien ne le dise. Tant qu'aucun formulaire
          // propre n'est choisi, l'éditeur le dit et renvoie à cette étape-là.
          formKey: "FO_MEETING_V2_COMPANION_FORM",
          target: "companionForm",
          translatable: false,
          help: "Le type, la largeur et l'ordre des champs, dans le formulaire de l'accompagnant. Sans formulaire propre au-dessus, il n'y a rien à ranger ici : c'est celui de l'étape informations qui lui est servi, et ses champs se règlent sur cette étape. Les questions elles-mêmes restent au back office : les renommer ici les renommerait pour tous les formulaires du domaine.",
        },
        {
          key: "FO_MEETING_V2_COMPANION_HTML",
          origin: {
            key: "FO_MEETING_COMPANIONS_TOP_HTML",
            note: "Vérifié : `screens/steps/Informations.jsx` de la V1 le préfère à `FO_MEETING_INFO_HTML` quand la participation vaut 5.",
          },
          label: "Message au-dessus de son formulaire",
          type: "html",
          target: "companionForm",
        },
        {
          key: "FO_MEETING_V2_COMPANION_COPY",
          origin: {
            key: "FO_MEETING_COMPANIONS_COPY_EXTENDED",
            note: "Vérifié : `screens/steps/Informations.jsx` de la V1 recopie `extended` du porteur sur le compte de l'accompagnant quand elle est vraie.",
          },
          label: "Reprendre vos jours et votre commande",
          type: "boolean",
          onByDefault: true,
          target: "companionSteps",
          help: "Ses jours de présence et ses articles s'ouvrent sur les vôtres : on vient ensemble, on prend souvent la même chose. Tout reste modifiable. Son FORMULAIRE, lui, reste vierge : une donnée personnelle recopiée depuis quelqu'un d'autre est une donnée que personne n'a vérifiée, et elle part sur un badge.",
        },
        {
          key: "FO_MEETING_V2_COMPANION_STEP_DATES_SHOW",
          label: "Lui demander ses jours",
          type: "boolean",
          onByDefault: true,
          target: "companionSteps",
          help: "Retiré, il est inscrit aux mêmes jours que la personne qui l'inscrit.",
        },
        {
          key: "FO_MEETING_V2_COMPANION_STEP_ARTICLES_SHOW",
          label: "Lui proposer la billetterie",
          type: "boolean",
          onByDefault: true,
          target: "companionSteps",
          help: "Ses articles tombent sur la facture du porteur : le serveur additionne déjà ceux de toutes les personnes qu'il a inscrites.",
        },
        {
          key: "FO_MEETING_V2_COMPANION_STEP_CONFERENCES_SHOW",
          label: "Lui faire choisir des conférences",
          type: "boolean",
          target: "companionSteps",
          help: "La V1 ne le propose pas. À n'ouvrir que si vos accompagnants assistent vraiment aux séances, sinon c'est une étape de plus à traverser pour rien.",
        },
        {
          key: "FO_MEETING_V2_EMAIL_COMPANION",
          label: "Email de confirmation de l'accompagnant",
          type: "action-email" as const,
          emailAction: "companion",
          target: "companionSteps",
          translatable: false,
          help: "Part quand son parcours est terminé (« Terminer pour cette personne »). Le même réglage que la page « Templates email » du back office : le compte qui envoie, et le modèle de chaque langue. Sans modèle, rien ne part et l'inscription se passe de lui.",
        },
      ],
    },
  ],

  conferences: [
    {
      needs: "FO_MEETING_V2_STEP_CONFERENCES_SHOW",
      title: "Étape · conférences",
      help: "Le programme vient du back office : les conférences, leurs créneaux et leurs quotas s'y règlent. Cette étape ne décide que de ce qui s'écrit autour.",
      settings: [
        {
          key: "FO_MEETING_V2_CONFERENCES_TITLE",
          label: "Titre",
          type: "text",
          fallback: "Le programme",
          target: "conferencesTitle",
        },
        {
          key: "FO_MEETING_V2_CONFERENCES_SUBTITLE",
          label: "Sous-titre",
          type: "text",
          multiline: true,
          fallback:
            "Choisissez un créneau par conférence. Rien n'est obligatoire.",
          target: "conferencesTitle",
        },
        {
          key: "FO_MEETING_V2_CONFERENCES_HTML",
          origin: {
            key: "FO_MEETING_CONFERENCES_V2_HTML",
            note: "Vérifié : `screens/steps/conferencesV2/index.jsx` de la V1 le rend en tête de l'écran. Le nom ne se déduit pas du nôtre : la V1 numérote son SYSTÈME (« _V2_ »), pas son front-office.",
          },
          label: "Message d'introduction",
          type: "html",
          target: "conferencesTitle",
          help: "Affiché au-dessus du programme, en HTML libre. Vide : rien ne s'affiche.",
        },
      ],
    },
  ],

  /**
   * LA CONFIRMATION — le dernier écran de l'inscription.
   *
   * Son texte change selon la réponse donnée à l'étape 1 : remercier de sa venue
   * quelqu'un qui vient de décliner est la faute que cet écran doit rendre
   * impossible. D'où deux messages, et non un seul.
   */
  confirmation: [
    {
      title: "Confirmation",
      settings: [
        {
          key: "FO_MEETING_V2_CONFIRMATION_TITLE",
          situations: ["onsite", "remote", "represented"],
          label: "Titre",
          type: "text",
          fallback: "Votre inscription est enregistrée",
          target: "confirmationTitle",
        },
        {
          key: "FO_MEETING_V2_CONFIRMATION_MESSAGE",
          situations: ["onsite", "remote", "represented"],
          label: "Message",
          type: "text",
          multiline: true,
          fallback: "Nous avons bien reçu votre réponse. À très bientôt.",
          target: "confirmationTitle",
        },
        {
          key: "FO_MEETING_V2_CONFIRMATION_MESSAGE_REMOTE",
          situations: ["remote"],
          label: "Message · en distanciel",
          type: "text",
          multiline: true,
          target: "confirmationTitle",
          help: "« À très bientôt sur place » lu par quelqu'un qui suivra en ligne est un petit mensonge. Vide : le message général s'applique.",
        },
        /* ⚠️ UN SEUL BLOC, LÀ OÙ LA V1 EN A QUATRE (`FO_MEETING_CONFIRMATION_PRESENT_HTML`,
           `_REMOTE_HTML`, `_NOT_PRESENT_HTML`, `_REPRESENTED_HTML`, un par réponse de
           l'étape 1). Aucune origine déclarée : reprendre l'une des quatre pour tous les
           cas ferait lire « à bientôt sur place » à qui a décliné. */
        {
          key: "FO_MEETING_V2_CONFIRMATION_HTML",
          label: "Message d'introduction",
          type: "html",
          target: "confirmationTitle",
          help: "Affiché sous le titre, avant le récapitulatif, en HTML libre. Vide : rien ne s'affiche.",
        },
        /**
         * ⚠️ THIS BUTTON DOES NOT ALWAYS SHOW, EVEN WHEN ON, and that is deliberate: it
         * leads to a badge that does not exist while the registration is unpaid — or at
         * all, if the person is not coming. The confirmation applies the SAME rule as
         * the badge screen (`badge/badge.ts`) rather than writing a second one: two
         * screens contradicting each other on "do you have a badge" is exactly what the
         * prototype's review found.
         *
         * No label setting: "badge" is the product's word, and the PAGE's name is
         * already set in "Pages du site". Interpolating it here would read "Voir Mon
         * accès".
         */
        {
          key: "FO_MEETING_V2_CONFIRMATION_BADGE_SHOW",
          // À distance, pas de badge (tâche #225, voir `badgeState`).
          situations: ["onsite"],
          label: "Bouton « Voir mon badge »",
          type: "boolean",
          onByDefault: true,
          target: "confirmationBadge",
          help: "Mène à la page Badge, où le participant retrouve son QR et le téléchargement. Il ne s'affiche pas tant qu'un solde reste dû, ni à quelqu'un qui ne vient pas ou qui suit à distance.",
          origin: {
            key: "FO_MEETING_CONFIRMATION_SHOW_DOWNLOAD_BADGE",
            note: "Vérifié : `screens/steps/Confirmation/index.jsx` de la V1 s'en sert pour montrer « Télécharger le badge » ET le QR sur sa confirmation, avec les mêmes réserves que la V2 applique (`!resume?.canPay`, et un gabarit configuré). La V2 mène à la PAGE badge, qui porte le téléchargement : c'est le même arbitrage, à savoir si le badge est proposé depuis la confirmation. À ne pas confondre avec `FO_MEETING_CONFIRMATION_SHOW_SEND_BADGE`, qui envoie le badge d'un accompagnant PAR E-MAIL.",
          },
        },
        {
          key: "FO_MEETING_V2_CONFIRMATION_PENDING_TITLE",
          situations: ["pending"],
          label: "Titre · règlement en attente",
          type: "text",
          fallback: "Votre inscription est presque complète",
          target: "confirmationTitle",
          help: "Lu par qui a répondu mais n'a pas encore payé. « Votre inscription est enregistrée » lui ferait croire que tout est en ordre, or le contrôle d'accès refuse l'entrée tant que le solde est dû.",
        },
        {
          key: "FO_MEETING_V2_CONFIRMATION_PENDING_MESSAGE",
          situations: ["pending"],
          label: "Message · règlement en attente",
          type: "text",
          multiline: true,
          fallback:
            "Votre réponse est bien enregistrée. Il reste le règlement pour la rendre définitive.",
          target: "confirmationTitle",
        },
        {
          key: "FO_MEETING_V2_CONFIRMATION_DECLINE_TITLE",
          situations: ["decline"],
          label: "Titre · en cas de refus",
          type: "text",
          fallback: "Merci de nous avoir prévenus",
          target: "confirmationTitle",
        },
        {
          key: "FO_MEETING_V2_CONFIRMATION_DECLINE_MESSAGE",
          situations: ["decline"],
          label: "Message · en cas de refus",
          type: "text",
          multiline: true,
          fallback:
            "Vous resterez informé par email, et le replay des conférences vous sera envoyé après l'événement.",
          target: "confirmationTitle",
        },
        {
          key: "FO_MEETING_V2_CONFIRMATION_RECAP",
          label: "Récapitulatif détaillé",
          type: "boolean",
          onByDefault: true,
          target: "confirmationRecap",
          help: "Ce qui a été enregistré, ligne à ligne. « Votre inscription est enregistrée » ne dit pas SOUS QUEL PROFIL ni à quelle adresse partira le badge.",
        },
        /**
         * ═══ LES LIGNES DU RÉCAPITULATIF, UNE PAR INTERRUPTEUR ═══
         *
         * Demandé le 2026-09-17 : le récapitulatif était tout ou rien. Chaque ligne a son
         * interrupteur, allumé par défaut — la carte des événements déjà réglés ne bouge
         * pas — et n'a de sens que si le récapitulatif s'affiche (`needs`). Une ligne
         * allumée reste soumise à la règle de la carte : sans valeur, elle ne s'affiche pas.
         */
        ...([
          ["NAME", "Nom", undefined],
          ["EMAIL", "Adresse email", undefined],
          ["PARTICIPATION", "Réponse de participation", "Sur place, à distance, représenté ou refus."],
          ["TYPOLOGY", "Profil", undefined],
          ["DATES", "Jours retenus", undefined],
          ["COMPANY", "Société", "La société pour laquelle la personne vient, si l'étape existe."],
          ["REASON", "Motif du refus", "Seulement pour qui ne vient pas, et si un motif a été donné."],
          ["CONFERENCES", "Conférences et créneaux", undefined],
          ["ARTICLES", "Commande et montants", "Les articles retenus, le total, les déductions et le reste à régler."],
          ["COMPANIONS", "Accompagnants", undefined],
          ["REPRESENTATION", "Représentation", "Qui vient à la place de la personne, si elle est représentée."],
        ] as const).map(([suffix, label, help]) => ({
          key: `FO_MEETING_V2_RECAP_${suffix}`,
          /* Deux lignes n'existent que pour une réponse : le motif pour qui ne vient pas,
             le représentant pour qui se fait représenter (voir `funnel/Recap.tsx`). */
          ...(suffix === "REASON"
            ? { situations: ["decline"] as const }
            : suffix === "REPRESENTATION"
              ? { situations: ["represented"] as const }
              : {}),
          needs: "FO_MEETING_V2_CONFIRMATION_RECAP",
          section: "Lignes du récapitulatif",
          label,
          type: "boolean" as const,
          onByDefault: true,
          target: "confirmationRecap",
          ...(help ? { help } : {}),
        })),
        /**
         * LES CHAMPS DU FORMULAIRE — dont les données étendues. Ce que l'étape informations
         * demande n'était jamais rappelé ici ; l'organisateur coche ce qui doit l'être.
         * Rien de coché, rien d'ajouté. Voir `funnel/recapFields.ts`.
         */
        {
          key: "FO_MEETING_V2_RECAP_FIELDS",
          needs: "FO_MEETING_V2_CONFIRMATION_RECAP",
          section: "Lignes du récapitulatif",
          label: "Champs du formulaire à afficher",
          type: "recap-fields",
          target: "confirmationRecap",
          help: "Les champs des formulaires d'informations servis dans le parcours, données étendues comprises. Cochés, ils s'ajoutent sous « Participant », après la société, avec leur libellé traduit. Nom, prénom et email ont leur propre interrupteur.",
        },
        {
          key: "FO_MEETING_V2_CONFIRMATION_EDIT",
          label: "Bouton « Modifier mon inscription »",
          type: "boolean",
          onByDefault: true,
          target: "confirmationEdit",
          help: "Ramène à la première étape. Le retirer n'empêche personne de changer d'avis : ça les oblige à vous écrire.",
        },
        {
          key: "FO_MEETING_V2_CONFIRMATION_BACK_SHOW",
          label: "Bouton de retour à l'accueil",
          type: "boolean",
          onByDefault: true,
          target: "confirmationBack",
        },
        {
          key: "FO_MEETING_V2_CONFIRMATION_BACK_LABEL",
          needs: "FO_MEETING_V2_CONFIRMATION_BACK_SHOW",
          label: "Libellé du bouton",
          type: "text",
          fallback: "Retour à l'accueil",
          target: "confirmationBack",
        },
      ],
    },
    /**
     * LES EMAILS DE LA CONFIRMATION — les mêmes actions que la V1, aux mêmes moments
     * (`routes/Confirmation.tsx`). Une ligne par action ; chacune ne part que si elle a
     * un modèle. Tant qu'il reste à payer, aucune ne part : c'est l'email « après
     * paiement » qui prend le relais (famille Paiement).
     */
    {
      title: "Emails de confirmation",
      help: "Envoyés une fois la réponse enregistrée, selon ce qu'elle est. Rien ne part tant qu'un paiement reste dû.",
      settings: [
        {
          key: "FO_MEETING_V2_EMAIL_ONSITE",
          situations: ["onsite"],
          label: "Inscription sur place",
          type: "action-email" as const,
          emailAction: "onsite",
          target: "confirmationTitle",
          translatable: false,
          help: "Le même réglage que la page « Templates email » du back office : le compte qui envoie, et le modèle de chaque langue. Sans modèle, rien ne part et l'inscription se passe de lui.",
        },
        {
          key: "FO_MEETING_V2_EMAIL_REMOTE",
          situations: ["remote"],
          label: "Inscription à distance",
          type: "action-email" as const,
          emailAction: "remote",
          target: "confirmationTitle",
          translatable: false,
          help: "Le même réglage que la page « Templates email » du back office : le compte qui envoie, et le modèle de chaque langue. Sans modèle, rien ne part et l'inscription se passe de lui. Ne concerne que la réponse « à distance », si elle est proposée.",
        },
        {
          key: "FO_MEETING_V2_EMAIL_DECLINE",
          situations: ["decline"],
          label: "Refus de participer",
          type: "action-email" as const,
          emailAction: "decline",
          target: "confirmationTitle",
          translatable: false,
          help: "Le même réglage que la page « Templates email » du back office : le compte qui envoie, et le modèle de chaque langue. Sans modèle, rien ne part et l'inscription se passe de lui.",
        },
        {
          key: "FO_MEETING_V2_EMAIL_DELEGATING",
          situations: ["represented"],
          label: "Se fait représenter (à la personne)",
          type: "action-email" as const,
          emailAction: "delegating",
          target: "confirmationTitle",
          translatable: false,
          help: "Le même réglage que la page « Templates email » du back office : le compte qui envoie, et le modèle de chaque langue. Sans modèle, rien ne part et l'inscription se passe de lui. La variable {{var:fromUser}} porte le nom de la personne représentée.",
        },
        {
          key: "FO_MEETING_V2_EMAIL_DELEGATE",
          situations: ["represented"],
          label: "Se fait représenter (à son représentant)",
          type: "action-email" as const,
          emailAction: "delegate",
          target: "confirmationTitle",
          translatable: false,
          help: "Envoyé à l'adresse du représentant saisie à l'étape participation, s'il y en a une. Le même réglage que la page « Templates email » du back office : le compte qui envoie, et le modèle de chaque langue. Sans modèle, rien ne part et l'inscription se passe de lui. La variable {{var:fromUser}} porte le nom de la personne représentée.",
        },
      ],
    },
    /**
     * LE PAIEMENT — sur la confirmation, et pas ailleurs.
     *
     * ⚠️ CE N'EST PAS UNE PRÉFÉRENCE DE MISE EN PAGE : le serveur facture ce qui est
     * ENREGISTRÉ, et la commande ne l'est qu'ici. Voir `funnel/payment.ts`.
     *
     * ⚠️ AUCUN RÉGLAGE DE MONTANT NI DE MOYEN DE PAIEMENT. Les prix viennent des
     * articles, la TVA du régime de facturation choisi par le payeur, et la clé Stripe
     * des paramètres de l'événement (`FO_PAY_STRIPE_PRIVATE_KEY`, chiffrée, réglée au
     * back office). Une clé secrète ne se saisit pas dans un panneau qui vit DANS le
     * front-office.
     *
     * ⚠️ SANS `situations` : la famille reste dans le panneau quelle que soit la version
     * de la confirmation choisie. L'organisateur règle le paiement depuis « Sur place »
     * aussi, la situation par défaut ; cachée là, la famille passait pour absente.
     */
    {
      title: "Paiement",
      help: "N'apparaît que s'il reste quelque chose à régler. Le paiement se fait chez Stripe, avec la clé réglée au back office ; les prix viennent des articles.",
      settings: [
        {
          key: "FO_MEETING_V2_PAY_SHOW",
          origin: {
            key: "FO_MEETING_CONFIRMATION_HIDE_PAY",
            // La V1 nomme ce qu'elle MASQUE, la V2 ce qu'elle AFFICHE.
            invert: true,
            note: "Vérifié : `screens/steps/resume/index.jsx` de la V1 n'affiche son bouton que si `isEnd && resume.canPay && !FO_MEETING_CONFIRMATION_HIDE_PAY`.",
          },
          label: "Proposer le paiement en ligne",
          type: "boolean",
          onByDefault: true,
          target: "confirmationPay",
          help: "Retiré, le solde reste dû : le participant devra régler autrement, et rien sur cet écran ne le lui dira. À n'utiliser que si vous encaissez par un autre canal.",
        },
        {
          key: "FO_MEETING_V2_PAY_LABEL",
          needs: "FO_MEETING_V2_PAY_SHOW",
          origin: {
            key: "FO_MEETING_ARTICLES_TO_PAY_LABEL",
            note: "Vérifié : `screens/steps/resume/index.jsx` de la V1 l'utilise comme intitulé du bouton, avec `t('PAY')` en repli.",
          },
          label: "Libellé du bouton",
          type: "text",
          fallback: "Payer",
          target: "confirmationPay",
        },
        {
          key: REGIMES_KEY,
          needs: "FO_MEETING_V2_PAY_SHOW",
          label: "Catégories de payeur proposées",
          type: "select-many",
          options: REGIMES.map((r) => ({
            value: String(r.type),
            label: r.label,
            labelKey: regimeLabelKey(r.type),
          })),
          target: "confirmationPay",
          help: "La liste « Vous réglez en tant que » du formulaire de facturation. Toutes sont proposées tant que rien n'est décoché. Toutes sont facturées TTC, sociétés hors de France comprises ; le n° de TVA intracommunautaire de ces dernières est facultatif. Une seule catégorie gardée est choisie d'office pour le payeur. Chaque catégorie cochée peut être renommée sous la liste.",
        },
        /**
         * LE NOM DE CHAQUE CATÉGORIE, tel que le payeur le lit.
         *
         * ⚠️ `managed` : dessinés SOUS les cases de la liste ci-dessus (voir `labelKey`),
         * pas en six lignes à part — et seulement pour les catégories cochées. Déclarés
         * quand même, pour que « Tout traduire » et la recherche les trouvent.
         *
         * ⚠️ SANS `fallback`, ET C'EST VOULU : vide, l'écran garde le mot du produit
         * (`TAX_REGIME_…`), déjà traduit. Un repli déclaré ici serait un second mot à semer
         * sous la clé du réglage (`i18n/couverture.test.ts`), que le site ne lirait pas.
         * Le panneau montre le nom d'origine en texte grisé (`Row`).
         */
        ...REGIMES.map((r) => ({
          key: regimeLabelKey(r.type),
          needs: "FO_MEETING_V2_PAY_SHOW",
          label: `${r.label} · libellé`,
          type: "text" as const,
          target: "confirmationPay",
          managed: true as const,
        })),
        {
          key: "FO_MEETING_V2_PAY_NOTICE",
          needs: "FO_MEETING_V2_PAY_SHOW",
          label: "Mention sous le paiement",
          type: "text",
          multiline: true,
          target: "confirmationPay",
          help: "Conditions d'annulation, délai de remboursement, contact de facturation. Vide : rien ne s'affiche.",
        },
        {
          key: "FO_MEETING_V2_EMAIL_PAID",
          needs: "FO_MEETING_V2_PAY_SHOW",
          label: "Email après paiement réussi",
          type: "action-email" as const,
          emailAction: "paid",
          target: "pay",
          translatable: false,
          help: "Le même réglage que la page « Templates email » du back office : le compte qui envoie, et le modèle de chaque langue. Sans modèle, rien ne part et l'inscription se passe de lui. Part au retour de Stripe, une fois par paiement ; il remplace l'email de confirmation, qui n'est pas envoyé tant qu'il reste à payer.",
        },
      ],
    },
    /**
     * LES ACCOMPAGNANTS — les gens qu'on a inscrits avec soi.
     *
     * ⚠️ CE GROUPE NE SERT QU'À LES MONTRER, pas à en ajouter. Les créer demande un
     * écran à eux (un formulaire par personne, un plafond, la propagation des jours et
     * des conférences) : c'est une étape du parcours, pas une section de la
     * confirmation. Ici on répond à « qui vient avec moi, déjà ? ».
     */
    {
      title: "Accompagnants",
      help: "Les personnes inscrites par ce participant, telles que le back office les connaît. Elles ne s'ajoutent pas depuis cet écran.",
      settings: [
        {
          key: "FO_MEETING_V2_COMPANIONS_SHOW",
          /* ⚠️ PAS D'ORIGINE ICI, ET C'EST LA BOUCLE DE VÉRIFICATION QUI L'A IMPOSÉ.
             `FO_MEETING_COMPANIONS_IS_ACTIVE` alimentait deux cibles à la fois — cette
             ligne et l'interrupteur de l'ÉTAPE — ce qu'`origins.test.ts` refuse : à la
             reprise, une clé V1 doit savoir où elle atterrit. L'étape l'emporte, parce
             que c'est elle qui fait exister la fonctionnalité ; ce rappel-ci n'est qu'un
             affichage, et la V1 n'a pas de réglage pour lui. */
          label: "Rappeler les accompagnants",
          type: "boolean",
          onByDefault: true,
          target: "confirmationCompanions",
          help: "La section disparaît d'elle-même quand la personne n'en a aucun.",
        },
        {
          key: "FO_MEETING_V2_COMPANIONS_LABEL",
          needs: "FO_MEETING_V2_COMPANIONS_SHOW",
          origin: {
            key: "FO_MEETING_COMPANIONS_LABEL",
            note: "Vérifié : `utils/settings.js` de la V1 en fait le mot qu'elle affiche partout où elle nomme la notion (`getCompanionLabel`).",
          },
          label: "Le mot de votre événement",
          type: "text",
          fallback: "Accompagnants",
          target: "confirmationCompanions",
          help: "« Accompagnants », « Invités », « Collaborateurs » : le mot change d'un événement à l'autre.",
        },
      ],
    },
    /**
     * LES FACTURES — le seul document COMPTABLE remis au participant.
     *
     * ⚠️ RIEN À RÉGLER SUR LEUR CONTENU : le document est composé par le serveur, à
     * partir de ce que la facture porte déjà (le numéro qu'il a attribué, les articles,
     * la TVA, les coordonnées données au paiement). Ce qui se décide ici, c'est
     * seulement de les proposer, et sous quel intitulé.
     */
    {
      title: "Factures",
      help: "N'apparaissent qu'une fois un paiement encaissé, et seulement les factures réglées : une facture en attente n'atteste de rien.",
      settings: [
        {
          key: "FO_MEETING_V2_INVOICES_SHOW",
          origin: {
            key: "FO_MEETING_INVOICES_HIDE",
            // La V1 nomme ce qu'elle MASQUE, la V2 ce qu'elle AFFICHE.
            invert: true,
            note: "Vérifié : `screens/steps/Confirmation/Invoices.jsx` de la V1 ne rend son bloc que si `invoices?.length > 0 && !FO_MEETING_INVOICES_HIDE`.",
          },
          label: "Proposer le téléchargement des factures",
          type: "boolean",
          onByDefault: true,
          target: "confirmationInvoices",
          help: "Retiré, le participant devra vous réclamer sa facture par email.",
        },
        {
          key: "FO_MEETING_V2_INVOICES_TITLE",
          needs: "FO_MEETING_V2_INVOICES_SHOW",
          label: "Intitulé du bloc",
          type: "text",
          fallback: "Vos factures",
          target: "confirmationInvoices",
        },
      ],
    },
  ],

  home: [
    {
      title: "Message d'accueil",
      settings: [
        {
          key: "FO_MEETING_V2_HOME_TITLE",
          label: "Titre",
          type: "text",
          target: "homeHero",
        },
        {
          key: "FO_MEETING_V2_HOME_SUBTITLE",
          label: "Sous-titre",
          type: "text",
          target: "homeHero",
        },
        {
          key: "FO_MEETING_V2_HOME_HTML",
          origin: {
            key: "FO_MEETING_HOME",
            note: "Vérifié : `hooks/useConf.js` de la V1 l'expose en `HOME_HTML` et `screens/Home.jsx` le rend en tête de l'accueil (`<HTML value={HOME_HTML} />`). Le nom fait penser à un titre, et un modèle l'avait d'ailleurs proposé pour un, mais c'est un bloc de HTML libre.",
          },
          label: "Bloc libre",
          type: "html",
          target: "homeHero",
          help: "Du HTML libre, affiché sous le message d'accueil : une annonce, un mot de l'organisateur, un lien. Vide : rien ne s'affiche.",
        },
      ],
    },
    {
      title: "Blocs de la page",
      settings: [
        {
          key: "FO_MEETING_V2_HOME_CTA",
          label: "Bouton principal",
          type: "boolean",
          target: "homeCta",
        },
        {
          key: "FO_MEETING_V2_HOME_CTA_TEXT",
          fallback: "Continuer",
          needs: "FO_MEETING_V2_HOME_CTA",
          label: "Libellé du bouton",
          type: "text",
          target: "homeCta",
        },
      ],
    },
    /**
     * ═══ ET CE QUE LIT QUELQU'UN QUI N'EST PAS ENCORE DES VÔTRES ═══
     *
     * Le même accueil, mais devant un public qui ne s'est pas inscrit : « Continuer »
     * ne veut rien dire pour qui n'a rien commencé, et « Bon retour parmi nous » est
     * faux. Trois textes de rechange, servis à la place des trois du dessus tant que
     * personne n'est connecté.
     *
     * ⚠️ VIDES, ILS RENDENT LA MAIN AUX PREMIERS. C'est ce qui permet d'ajouter cette
     * famille sans changer un seul accueil existant : un événement qui n'écrit rien
     * ici sert exactement ce qu'il servait. Écrire, c'est différencier.
     *
     * ⚠️ SANS RAPPORT AVEC L'INTERRUPTEUR « PUBLIC » DE L'ACCUEIL, et c'est voulu : ces
     * textes servent à quiconque n'est PAS CONNECTÉ, y compris sur une section ouverte
     * qui renvoie vers l'accueil. L'interrupteur décide qui peut entrer, ceux-ci
     * décident ce qu'on lui dit.
     */
    {
      title: "Accueil · visiteur non connecté",
      help: "Ce que lit quelqu'un qui n'a pas de compte. Laissé vide, c'est le message d'accueil ordinaire qui s'affiche. L'aperçu « Vu par » de la barre montre le résultat.",
      settings: [
        {
          key: "FO_MEETING_V2_HOME_PUBLIC_TITLE",
          label: "Titre",
          type: "text",
          target: "homeHero",
          help: "Vide : le titre d'accueil ordinaire.",
        },
        {
          key: "FO_MEETING_V2_HOME_PUBLIC_SUBTITLE",
          label: "Sous-titre",
          type: "text",
          target: "homeHero",
          help: "Vide : le sous-titre d'accueil ordinaire.",
        },
        {
          /* ⚠️ PAS D'`origin` : la V1 n'a qu'un accueil, déjà repris par le bloc libre
             ordinaire (`FO_MEETING_HOME`). Le proposer ici aussi ferait recopier le
             même texte à deux endroits, et la reprise n'a pas à trancher lequel des
             deux l'organisateur voulait. */
          key: "FO_MEETING_V2_HOME_PUBLIC_HTML",
          label: "Bloc libre",
          type: "html",
          target: "homeHero",
          help: "Ce que lit un visiteur sans compte, à la place du bloc libre ordinaire : le programme en deux lignes, un argument, un lien. Vide : c'est le bloc libre ordinaire qui s'affiche.",
        },
        {
          key: "FO_MEETING_V2_HOME_PUBLIC_CTA_TEXT",
          needs: "FO_MEETING_V2_HOME_CTA",
          label: "Libellé du bouton",
          type: "text",
          target: "homeCta",
          help: "« Continuer » ne veut rien dire pour qui n'a rien commencé. Vide : le libellé ordinaire.",
        },
      ],
    },
  ],

  /**
   * ═══ MY BADGE ═══
   *
   * ⚠️ WHAT IS NOT HERE MATTERS AS MUCH AS WHAT IS. The badge is a BUSINESS STATE: it
   * is only issued against a real registration with no balance due. No setting lifts
   * that lock, and none should be added — an organiser flipping it would show their
   * participants a pass that access control will refuse, in front of everybody. See
   * `badge/badge.ts`.
   *
   * ⚠️ THE PAGE'S NAME IS NOT SET HERE EITHER: the "Pages du site" manager carries it
   * (`FO_MEETING_V2_PAGE_BADGE_LABEL`), as it does for the other eleven. Two places to
   * name one page means two names that contradict each other.
   */
  /**
   * ═══ LE PROGRAMME — UNE PAGE DU SITE, PAS UNE ÉTAPE ═══
   *
   * ⚠️ AUCUNE `origin` : la V1 n'a pas cet écran. Elle n'a que l'étape du tunnel
   * (`screens/Conferences/`) ; la page de consultation est une addition de la V2, tirée
   * de la maquette. Déclarer une correspondance ici reviendrait à en inventer une.
   */
  /**
   * ═══ LE CARNET DE RENCONTRES ═══
   *
   * ⚠️ PAS DE RÉGLAGE « SOCIÉTÉ / FONCTION / E-MAIL », À LA DIFFÉRENCE DE LA MAQUETTE.
   * Elle en propose quatre, en booléens ; le produit, lui, laisse choisir N'IMPORTE QUEL
   * champ — natif ou étendu — au back office (`FO_MEETING_CONTACT_DISPLAY_FIELDS`), et
   * c'est le SERVEUR qui les résout, libellés traduits compris. Quatre interrupteurs V2
   * auraient soit ignoré cette configuration, soit prétendu la piloter sans que personne
   * ne les lise. Ce qu'un badge scanné révèle de quelqu'un n'a qu'une réponse.
   */
  /**
   * ═══ SCANNER UN BADGE ═══
   *
   * ⚠️ PAS DE RÉGLAGE POUR LA CAMÉRA. La maquette en met un ; il ne gouvernerait rien —
   * une caméra est là ou n'est pas là, et le navigateur seul décide de l'autoriser. Un
   * interrupteur « caméra » aurait promis un pouvoir que l'organisateur n'a pas.
   */
  scan: [
    {
      title: "Titre de l'écran",
      settings: [
        {
          key: "FO_MEETING_V2_SCAN_TITLE",
          label: "Titre",
          type: "text",
          fallback: "Scanner un badge",
          target: "scanTitle",
        },
        {
          key: "FO_MEETING_V2_SCAN_SUBTITLE",
          label: "Sous-titre",
          type: "text",
          multiline: true,
          fallback: "Visez le QR du badge de votre interlocuteur.",
          target: "scanTitle",
        },
        {
          key: "FO_MEETING_V2_SCAN_HTML",
          label: "Message d'introduction",
          type: "html",
          target: "scanTitle",
          help: "Affiché sous le titre, avant la caméra, en HTML libre. Vide : rien ne s'affiche.",
        },
      ],
    },
    {
      title: "Saisie",
      settings: [
        {
          key: "FO_MEETING_V2_SCAN_MANUAL",
          label: "Autoriser le code manuel",
          type: "boolean",
          onByDefault: true,
          target: "scanManual",
          help: "Indispensable quand la caméra est refusée, absente, ou que le badge est abîmé. L'éteindre laisse sans recours sur un ordinateur de bureau.",
        },
      ],
    },
  ],

  /**
   * ═══ L'ALBUM ═══
   *
   * ⚠️ PAS DE RÉGLAGE « TÉLÉCHARGEMENT GROUPÉ » NI « BORNE », à la différence de la
   * maquette. Le premier demande une archive que l'API ne fabrique pas — enchaîner N
   * téléchargements se fait bloquer par le navigateur dès le second — et le mode borne
   * est un écran à part entière. Un interrupteur qui ne commande rien est une promesse
   * vide dans le panneau.
   *
   * ⚠️ NI « ACCÈS PAR CODE BADGE ». La route l'accepte déjà (`?badge=`, elle est
   * `isPrivateAndPublic`), mais cette page vit derrière la session comme le reste du
   * site : le réglage annoncerait un accès public qui n'existe pas encore.
   */
  album: [
    {
      title: "Titre de l'écran",
      settings: [
        {
          key: "FO_MEETING_V2_ALB_TITLE",
          label: "Titre",
          type: "text",
          fallback: "Mon album",
          target: "albTitle",
        },
        {
          key: "FO_MEETING_V2_ALB_SUBTITLE",
          label: "Sous-titre",
          type: "text",
          multiline: true,
          fallback:
            "Les photos où vous apparaissez, reconnues automatiquement.",
          target: "albTitle",
        },
        {
          key: "FO_MEETING_V2_ALB_HTML",
          label: "Message d'introduction",
          type: "html",
          target: "albTitle",
          help: "Affiché sous le titre, avant les photos, en HTML libre. Vide : rien ne s'affiche.",
        },
      ],
    },
    {
      title: "La grille",
      settings: [
        {
          /**
           * Sur bureau seulement : le mobile en prend la moitié, arrondie au-dessus
           * (voir `album/colonnes.ts`). Bornes 1 à 8 ; hors bornes, la plus proche.
           */
          key: "FO_MEETING_V2_ALB_COLUMNS",
          label: "Nombre de colonnes",
          type: "number",
          fallback: "4",
          target: "albGrid",
          help: "Photos de front sur bureau, de 1 à 8. Le mobile en affiche la moitié (deux pour quatre, trois pour six). Vide : 4.",
        },
      ],
    },
    {
      title: "Les onglets",
      settings: [
        {
          /**
           * ⚠️ ALLUMÉ PAR DÉFAUT : c'est l'album tel qu'il était, et un événement déjà
           * ouvert ne doit pas perdre un onglet sans que personne l'ait décidé.
           */
          key: "FO_MEETING_V2_ALB_AMBIANCE",
          label: "Afficher l'onglet « Ambiance »",
          type: "boolean",
          onByDefault: true,
          target: "albTabs",
          help: "Éteint, il ne reste que « Toutes » et « Mes photos ». Les photos d'ambiance ne disparaissent pas pour autant : elles restent dans « Toutes ».",
        },
      ],
    },
    {
      title: "Reconnaissance faciale",
      help: "La phrase qui explique pourquoi l'album se remplit tout seul. Le tri des photos, lui, se règle au back office : cet interrupteur ne fait qu'afficher ou taire la mention.",
      settings: [
        {
          /**
           * ⚠️ ÉTEINT PAR DÉFAUT (2026-09-22). La mention prenait un rang entier au-dessus
           * des onglets pour dire ce que le sous-titre dit déjà — et, la ligne-portrait
           * une fois repliée, elle restait seule à occuper l'espace. Elle ne s'affiche plus
           * que si l'organisateur l'allume : l'interrupteur et son texte restent à portée.
           */
          key: "FO_MEETING_V2_ALB_IA",
          label: "Afficher la mention",
          type: "boolean",
          onByDefault: false,
          target: "albIA",
        },
        {
          key: "FO_MEETING_V2_ALB_IA_TEXT",
          label: "Mention affichée",
          type: "text",
          multiline: true,
          fallback:
            "Vos photos arrivent au fil de l'événement, dès que vous avez été reconnu.",
          needs: "FO_MEETING_V2_ALB_IA",
          target: "albIA",
        },
        {
          /**
           * ⚠️ ÉTEINT PAR DÉFAUT. Allumé, chaque participant peut envoyer un portrait à
           * la reconnaissance : c'est une donnée personnelle de plus et un coût de
           * traitement, donc une décision d'organisateur, pas un état de départ.
           */
          key: "FO_MEETING_V2_ALB_FACE",
          label: "Laisser choisir sa photo",
          type: "boolean",
          target: "albPortrait",
          help: "Un bloc en tête de l'album où le participant pose une photo de lui. La recherche part aussitôt et ne traite QUE lui ; l'écran l'affiche en cours puis annonce le résultat. Sans cela, seuls les portraits ajoutés au back office permettent de le reconnaître.",
        },
      ],
    },
  ],

  /**
   * ═══ LE REPLAY ═══
   *
   * ⚠️ UNE VIDÉO ET UN MESSAGE, PAS UNE LISTE DE SÉANCES. Le prototype dessine des
   * cartes « 58 min · Grand auditorium », mais rien dans le produit ne les porte :
   * `EventConference` n'a ni adresse de vidéo ni durée. La V1, elle, sert exactement
   * ceci (`screens/Replay.jsx`) : un HTML au-dessus, une adresse Vimeo dans un cadre.
   * On reprend ses deux réglages, plus le titre et le sous-titre du prototype.
   *
   * ⚠️ LA V1 A DEUX NOMS POUR CHAQUE CLÉ. Le back office actuel déclare
   * `FO_MEETING_REPLAY_VIMEO_URL` / `FO_MEETING_REPLAY_HTML`
   * (`api/migrations/20250926052407_SettingsFo-addData.js`) ; l'ancien écrivait
   * `replayVimeoUrl` / `replayHtml`, et c'est CE nom-là que le site V1 lit encore.
   * L'origine déclarée ici est celle du back office actuel. L'écran ne lit QUE les
   * clés V2 : il lisait aussi les deux noms V1 en repli, retiré le 2026-09-26 — la page
   * montrait un message et une vidéo que le panneau ne montrait pas.
   */
  replay: [
    {
      title: "Titre de l'écran",
      settings: [
        {
          key: "FO_MEETING_V2_REP_TITLE",
          label: "Titre",
          type: "text",
          fallback: "Replay",
          target: "repTitle",
        },
        {
          key: "FO_MEETING_V2_REP_SUBTITLE",
          label: "Sous-titre",
          type: "text",
          multiline: true,
          /* Le prototype promet « disponibles 90 jours » : c'est un engagement de
             l'organisateur, pas un défaut du produit. */
          fallback: "Les conférences filmées, à revoir à votre rythme.",
          target: "repTitle",
        },
      ],
    },
    {
      title: "La vidéo",
      help: "Une seule vidéo, dans un cadre sous le message. Sans adresse, la page dit que le replay n'est pas encore disponible.",
      settings: [
        {
          key: "FO_MEETING_V2_REP_HTML",
          origin: {
            key: "FO_MEETING_REPLAY_HTML",
            note: "Vérifié : `screens/Replay.jsx` de la V1 le rend en tête de l'écran (`<HTML name=\"replayHtml\" />`) ; le back office actuel le déclare sous ce nom (`SettingsFo-addData.js`, « Message au-dessus du replay »). L'ancien nom `replayHtml` n'est pas repris.",
          },
          label: "Message au-dessus de la vidéo",
          type: "html",
          target: "repHtml",
          help: "En HTML libre. Vide : rien ne s'affiche.",
        },
        {
          key: "FO_MEETING_V2_REP_URL",
          origin: {
            key: "FO_MEETING_REPLAY_VIMEO_URL",
            note: "Vérifié : `screens/Replay.jsx` de la V1 pose `replayVimeoUrl` tel quel en `src` d'une iframe ; le back office actuel le déclare sous ce nom (`SettingsFo-addData.js`, « Replay Vimeo URL »). L'ancien nom `replayVimeoUrl` n'est pas repris.",
          },
          label: "Adresse de la vidéo",
          type: "text",
          fallback: "",
          /* Une adresse n'est pas de la prose : c'est la même vidéo en six langues.
             Voir `FO_MEETING_V2_LIVE_URL`, même raison. */
          translatable: false,
          target: "repVideo",
          help: "Vimeo ou YouTube, y compris le lien de la barre d'adresse ; une adresse de lecteur (player.vimeo.com, avec son jeton) est gardée telle quelle. Toute autre adresse https est posée dans le cadre sans transformation.",
        },
        {
          key: "FO_MEETING_V2_REP_EMPTY",
          label: "Message sans vidéo",
          type: "text",
          multiline: true,
          fallback: "Le replay n'est pas encore disponible. Revenez d'ici quelques jours.",
          target: "repVideo",
          help: "Affiché à la place du cadre tant qu'aucune adresse n'est réglée.",
        },
      ],
    },
  ],

  /**
   * ═══ EN DIRECT ═══
   *
   * ⚠️ CE QUI SE RÈGLE ICI EST LA FORME DE L'ÉCRAN, PAS SON ÉTAT. Ce que la salle
   * regarde, quelle activité est ouverte, si les résultats sont montrés : rien de
   * tout cela n'est un réglage, c'est la régie qui le décide, en direct. Le
   * prototype le dit dans son aide (« en production, la bascule se fait depuis la
   * régie ») ; les mettre ici en ferait des valeurs qu'un organisateur croirait
   * pouvoir préparer la veille.
   *
   * ⚠️ ET IL N'Y A PAS D'HEURE DE DÉBUT. La V1 en a une, compare l'horloge du
   * NAVIGATEUR à ce réglage toutes les dix secondes, et fait donc démarrer le
   * direct à des moments différents selon les téléphones. Un participant en avance
   * d'une minute voit le direct, son voisin non. L'ouverture est un geste.
   */
  live: [
    {
      title: "Titre de l'écran",
      settings: [
        {
          key: "FO_MEETING_V2_LIVE_TITLE",
          label: "Titre",
          type: "text",
          fallback: "En direct",
          target: "liveTitre",
        },
        {
          key: "FO_MEETING_V2_LIVE_SUBTITLE",
          label: "Sous-titre",
          type: "text",
          multiline: true,
          fallback: "Suivez la session et participez depuis votre téléphone.",
          target: "liveTitre",
        },
        {
          key: "FO_MEETING_V2_LIVE_HTML",
          label: "Message d'introduction",
          type: "html",
          target: "liveTitre",
          help: "Affiché sous le titre, avant la vidéo, en HTML libre. Vide : rien ne s'affiche.",
        },
      ],
    },
    {
      title: "Vidéo",
      help: "La diffusion elle-même, pour ceux qui suivent à distance : un participant inscrit en présentiel est dans la salle et ne la voit pas. Coupée, il ne reste que le module interactif, ce qui est le cas d'une salle qui suit sur scène.",
      settings: [
        {
          key: "FO_MEETING_V2_LIVE_VIDEO",
          label: "Afficher la vidéo",
          type: "boolean",
          onByDefault: true,
          target: "liveVideo",
          origin: {
            key: "hideVideoLive",
            invert: true,
            note: "Vérifié : `screens/live/live.tsx` de la V1 masque `<Video/>` quand `hideVideoLive` est vrai. La V2 nomme ce qu'elle AFFICHE.",
          },
        },
        {
          key: "FO_MEETING_V2_LIVE_SOURCE",
          label: "Source de diffusion",
          type: "select-text",
          options: [
            { value: "youtube", label: "YouTube" },
            { value: "vimeo", label: "Vimeo" },
            { value: "dacast", label: "Dacast" },
            { value: "internal", label: "Flux interne" },
          ],
          needs: "FO_MEETING_V2_LIVE_VIDEO",
          target: "liveVideo",
          help: "La V1 la devine dans l'ordre de ses `if` (Dacast, puis Vimeo, puis YouTube) et cache le flux interne derrière un `?mode=internal` non documenté. Ici elle se choisit.",
        },
        {
          key: "FO_MEETING_V2_LIVE_URL",
          label: "Adresse du flux",
          type: "text",
          fallback: "",
          /* Une adresse de flux n'est pas de la prose : `youtube.com/embed/...` est la
             même vidéo en six langues. Sans ça, le panneau proposait de la traduire,
             et l'écran ne la voyait que dans la langue où elle avait été saisie. */
          translatable: false,
          needs: "FO_MEETING_V2_LIVE_VIDEO",
          target: "liveVideo",
          help: "L'identifiant ou l'adresse complète fournie par la plateforme : les deux sont acceptés, y compris le lien de la barre d'adresse. Vide : pas de vidéo sur le direct.",
          origin: {
            key: "youtubeId",
            note: "Vérifié : `screens/live/video.js` de la V1 construit `youtube.com/embed/${youtubeId}`. ⚠️ La V1 range sa vidéo dans TROIS clés (`youtubeId`, `vimeoUrl`, `FO_MEETING_DACAST_ID`) et déduit la source de celle qui est remplie ; la reprise ne connaît que la première. Les deux autres ne sont PLUS lues à l'affichage (retiré le 2026-09-26) : l'écran montrait une vidéo que le panneau ne montrait pas.",
          },
        },
        {
          key: "FO_MEETING_V2_LIVE_COUNT",
          label: "Compteur de spectateurs",
          type: "boolean",
          needs: "FO_MEETING_V2_LIVE_VIDEO",
          target: "liveVideo",
          help: "Compte tout le monde qui a la page ouverte, vous compris. Pas les écrans de salle : un vidéoprojecteur n'est pas une personne. Tant que personne n'est connecté, rien ne s'affiche : annoncer « 0 personne regarde » devant une salle pleine serait pire que se taire. Le nombre est repris sur l'écran de régie.",
        },
      ],
    },
    {
      title: "Avant et après",
      help: "Ce que lit un participant qui arrive trop tôt, ou trop tard. C'est la régie qui bascule d'un état à l'autre.",
      settings: [
        {
          key: "FO_MEETING_V2_LIVE_BEFORE",
          label: "Message avant le direct",
          /* ⚠️ DU HTML, comme son origine V1 (`htmlBeforeLive`). Typé `text`, la
             reprise recopiait des balises que React échappe : le participant lisait
             « <p>La diffusion commence à 14h00</p> », balises comprises. Et
             l'organisateur avait ici la seule zone de prose du produit sans éditeur,
             donc sans gras ni lien. */
          type: "html",
          fallback: "La diffusion n'a pas encore commencé.",
          target: "liveTitre",
          origin: {
            key: "htmlBeforeLive",
            note: "Vérifié : `screens/live/live.tsx` de la V1 sert ce bloc tant que `startAt` n'est pas passé.",
          },
        },
        {
          key: "FO_MEETING_V2_LIVE_AFTER",
          label: "Message après le direct",
          /* Du HTML, comme son origine V1 `FO_MEETING_LIVE_IS_CLOSE_HTML`. */
          type: "html",
          fallback: "La diffusion est terminée. Merci de votre participation.",
          target: "liveTitre",
          origin: {
            key: "FO_MEETING_LIVE_IS_CLOSE_HTML",
            note: "Vérifié : `screens/live/live.tsx` de la V1 sert ce HTML à la place de tout l'écran quand `FO_MEETING_LIVE_IS_CLOSE` est vrai.",
          },
        },
      ],
    },
    {
      title: "Module interactif",
      help: "Le cadre sous la vidéo, où s'ouvrent les sondages, quiz, votes, nuages de mots et questions que la régie met à l'écran.",
      settings: [
        {
          key: "FO_MEETING_V2_LIVE_NO_MODULE",
          label: "Message sans module",
          type: "text",
          multiline: true,
          fallback: "La régie n'affiche aucun module pour l'instant.",
          target: "liveModule",
          help: "Affiché aux participants pendant le direct, à la place du module, tant que la régie n'a mis aucune activité à l'écran. L'écran de salle, lui, garde son écran d'attente.",
        },
      ],
    },
    {
      title: "Réactions",
      help: "Les émoticônes que le public envoie pendant la session. Elles accompagnent l'activité en cours au lieu de la remplacer : la V1 en fait un écran à part alors qu'elle les colle déjà sous chacun de ses modules.",
      settings: [
        {
          key: "FO_MEETING_V2_LIVE_REACTIONS",
          label: "Autoriser les réactions",
          type: "boolean",
          onByDefault: true,
          target: "liveModule",
          help: "Elles n'apparaissent que si une séquence est ouverte au back office : sans séquence, un clic n'aurait nulle part où être rangé.",
        },
      ],
    },
  ],

  /**
   * ═══ L'ÉCRAN DE SALLE ═══
   *
   * ⚠️ SA PROPRE PORTÉE, ET PAS UN GROUPE DE PLUS SOUS « EN DIRECT ». Le sélecteur
   * d'écrans de la barre le propose comme un écran à part entière ; sans portée à lui,
   * le choisir ouvrait un panneau VIDE, ce qui se lit comme « cet écran ne se règle
   * pas » alors qu'il se règle.
   */
  scene: [
    {
      title: "Écran de salle",
      help: "Le vidéoprojecteur. Il ne se règle presque pas, et c'est voulu : ce qu'il affiche est décidé en direct depuis la régie, pas préparé la veille. Il s'ouvre avec le code de l'écran de salle : ouvrez /ecran sur le poste et tapez le code affiché sur la régie. Son apparence se personnalise comme le reste du site : survolez un bloc et choisissez « Personnaliser l'apparence », ou visez un élément précis depuis « Le site → Apparence sur mesure ». Sans jeton d'écran sur ce navigateur, l'aperçu montre l'écran d'attente.",
      settings: [
        {
          key: "FO_MEETING_V2_LIVE_SCENE_WAITING",
          label: "Contenu affiché entre deux activités",
          /* ⚠️ IL REMPLACE LE QR CODE ET L'ADRESSE DU DIRECT, retirés de l'attente
             (tâche #374), PUIS L'ACCROCHE ET LE NOM DE L'ÉVÉNEMENT (tâche #453) : ce qui
             s'y montre est entièrement à l'organisateur. */
          type: "html",
          /* ⚠️ UNE CIBLE, DEPUIS QUE L'ÉCRAN D'ATTENTE EST UN BLOC NOMMÉ. L'organisateur
             l'atteint par le sélecteur d'écrans, et la carte qui s'y ouvre est aussi la
             porte de « Personnaliser l'apparence » : sans réglage pointé sur le bloc, la
             carte ne s'ouvrait pas, et l'écran de salle ne s'habillait pas. */
          target: "sceneStage",
          help: "Seul contenu de l'écran de salle avant le direct et pendant, tant que la régie n'a lancé aucune activité : un titre, l'adresse du direct, votre QR code en image, un programme… Rien d'autre ne s'y ajoute. Vide : l'écran n'affiche que son fond et ses bandeaux.",
        },
        {
          key: "FO_MEETING_V2_LIVE_SMS_NUMBER",
          label: "Numéro du vote par SMS",
          type: "text",
          fallback: "",
          /* Un numéro n'est pas de la prose, et il n'y a qu'une carte SIM : même
             numéro dans toutes les langues (voir `STRUCTURAL_KEYS`). */
          translatable: false,
          help: "Affiché sur l'écran de salle et sur les téléphones tant que la régie a ouvert la réception des SMS : chaque réponse d'un sondage ou d'un quiz y prend un numéro (1, 2, 3…), et la consigne dit de l'envoyer par SMS à ce numéro. Les téléphones passent alors en lecture seule : on vote par SMS, plus par les boutons. Un seul vote par numéro et par activité. Vide : la consigne s'affiche sans numéro.",
          /* La consigne s'affiche dans le module de l'activité, côté salle. */
          target: "sceneModule",
        },
      ],
    },
    /**
     * ═══ LES BANDEAUX DE LA SALLE, PAS CEUX DU SITE ═══
     *
     * ⚠️ DES CLÉS À ELLES, et c'est tout l'objet de la famille. Le bandeau du site a
     * été retiré de l'écran nu exprès (voir `Shell`) : 180 px de communication pensés
     * pour un téléphone, projetés en haut d'un mur de plénière. L'organisateur veut
     * pourtant habiller son mur d'un haut et d'un bas — une image faite pour lui, en
     * 16/9, que le site n'a pas à porter. Les mêmes réglages que le site (image, zone à
     * garder, couleur des côtés, hauteur), le même composant pour les dessiner
     * (`site/Band.tsx`), sous d'autres clés.
     *
     * ⚠️ PAS D'INTERRUPTEUR : l'image vide éteint le bandeau. Celui du pied de page du
     * site en a un parce qu'il éteint aussi des coordonnées et des mentions ; ici, il
     * n'y a que l'image.
     */
    {
      title: "Bandeaux de l'écran de salle",
      help: "Une image en haut et une image en bas du mur, sur toute la largeur, quelle que soit l'activité à l'écran. Elles sont propres à l'écran de salle : les bandeaux du site ne s'y affichent pas. Les bandeaux gardent la hauteur réglée : c'est ce qui est projeté entre les deux — l'attente, l'activité, le message de fin — qui s'adapte à la place qu'ils laissent. Vide : pas de bandeau.",
      settings: [
        {
          key: "FO_MEETING_V2_LIVE_SCENE_HEADER_IMAGE",
          section: "En haut",
          label: "Image d'en-tête",
          keywords: ["logo"],
          type: "upload",
          safeKey: "FO_MEETING_V2_LIVE_SCENE_HEADER_IMAGE_SAFE",
          maxWidth: BAND_MAX_WIDTH,
          sidesKey: "FO_MEETING_V2_LIVE_SCENE_HEADER_IMAGE_SIDES",
          target: "sceneHeaderImage",
          help: "Bandeau affiché tout en haut de l'écran de salle, cadré comme celui du site : tracez dans l'aperçu la zone qui doit rester visible en entier (un logo, un titre) ; s'il manque de l'image sur les côtés, la couleur de son bord est prolongée, et vous pouvez en imposer une autre. Vide : pas de bandeau.",
        },
        {
          key: "FO_MEETING_V2_LIVE_SCENE_HEADER_IMAGE_HEIGHT",
          section: "En haut",
          label: "Hauteur du bandeau",
          type: "number",
          fallback: "180",
          target: "sceneHeaderImage",
          help: "En pixels, entre 40 et 600 ; un écran étroit en prend les deux tiers. C'est un maximum : si l'image, une fois cadrée, est moins haute, le bandeau prend sa hauteur. Vide : 180 px.",
        },
        {
          key: "FO_MEETING_V2_LIVE_SCENE_FOOTER_IMAGE",
          section: "En bas",
          label: "Image de pied de page",
          type: "upload",
          safeKey: "FO_MEETING_V2_LIVE_SCENE_FOOTER_IMAGE_SAFE",
          maxWidth: BAND_MAX_WIDTH,
          sidesKey: "FO_MEETING_V2_LIVE_SCENE_FOOTER_IMAGE_SIDES",
          target: "sceneFooterImage",
          help: "Bandeau affiché tout en bas de l'écran de salle, cadré exactement comme celui du haut. Vide : pas de bandeau.",
        },
        {
          key: "FO_MEETING_V2_LIVE_SCENE_FOOTER_IMAGE_HEIGHT",
          section: "En bas",
          label: "Hauteur du bandeau",
          type: "number",
          fallback: "180",
          target: "sceneFooterImage",
          help: "En pixels, entre 40 et 600 ; un écran étroit en prend les deux tiers. C'est un maximum : si l'image, une fois cadrée, est moins haute, le bandeau prend sa hauteur. Vide : 180 px.",
        },
      ],
    },
  ],

  /**
   * ═══ LA RÉGIE ═══
   *
   * Elle ne porte AUCUN réglage, et le dire vaut mieux que de laisser un panneau vide :
   * tout ce qui la concerne se décide en direct, sur elle. Le prototype fait pareil.
   *
   * ⚠️ LE CODE DE L'ÉCRAN DE SALLE N'EST PLUS ICI (#375) : il est sur la régie elle-même
   * (`routes/LiveRegie.tsx`). L'aide le dit, pour qui vient l'y chercher.
   */
  regie: [
    {
      title: "La régie",
      help: "Cet écran est un outil d'organisateur : il n'apparaît jamais dans la navigation d'un participant, et le serveur le refuse à qui n'a pas de rôle sur le domaine. Il ne se règle pas, il se pilote. Le code de l'écran de salle s'y trouve, en bas de la régie. La modération des questions, elle, s'allume sur l'écran « En direct ».",
      settings: [],
    },
  ],

  moderation: [
    {
      title: "La modération",
      help: "Cet écran est un outil d'organisateur, comme la régie : il ne se règle pas, il se pilote. Ce que les participants LISENT de la modération (le titre de l'activité, sa consigne) se règle sur l'activité elle-même, depuis la régie.",
      settings: [],
    },
  ],

  contacts: [
    {
      title: "Titre de l'écran",
      settings: [
        {
          key: "FO_MEETING_V2_CT_TITLE",
          label: "Titre",
          type: "text",
          fallback: "Mes contacts",
          target: "ctTitle",
        },
        {
          key: "FO_MEETING_V2_CT_SUBTITLE",
          label: "Sous-titre",
          type: "text",
          multiline: true,
          fallback: "Les badges que vous avez scannés.",
          target: "ctTitle",
        },
        {
          key: "FO_MEETING_V2_CT_HTML",
          label: "Message d'introduction",
          type: "html",
          target: "ctTitle",
          help: "Affiché sous le titre, avant le carnet, en HTML libre. Vide : rien ne s'affiche.",
        },
      ],
    },
    {
      title: "Notes et export",
      help: "La note se relit et se corrige sur la fiche, et part dans l'export. Les CHAMPS de la fiche, eux, se choisissent au back office : le serveur les résout, libellés compris.",
      settings: [
        {
          key: "FO_MEETING_V2_CT_COMMENT",
          label: "Note personnelle par contact",
          type: "boolean",
          onByDefault: true,
          target: "ctFiche",
          help: "Un mot pour se souvenir d'une rencontre. Éteinte, les notes déjà prises restent en base et reviennent avec le réglage.",
        },
        {
          key: "FO_MEETING_V2_CT_EXPORT",
          label: "Export du carnet",
          type: "boolean",
          onByDefault: true,
          target: "ctExport",
          help: "Un classeur reprenant EXACTEMENT les champs de la fiche, dans l'ordre, plus la date et la note.",
        },
        {
          key: "FO_MEETING_V2_CT_EXPORT_LABEL",
          label: "Libellé du bouton d'export",
          type: "text",
          fallback: "Exporter (XLSX)",
          needs: "FO_MEETING_V2_CT_EXPORT",
          target: "ctExport",
        },
      ],
    },
  ],

  programme: [
    /* ⚠️ PAS DE RÉGLAGE « AGENDA PERSONNEL ». Il gouvernait les deux onglets, retirés le
       2026-08-05 : un réglage qui ne commande plus rien est une promesse vide dans le
       panneau, et c'est exactement ce que ce catalogue s'interdit. Ce qu'il apportait est
       maintenant dans la liste, où une séance retenue se voit. */
    {
      title: "Titre de l'écran",
      settings: [
        {
          key: "FO_MEETING_V2_PROG_TITLE",
          label: "Titre",
          type: "text",
          fallback: "Programme",
          target: "progTitle",
        },
        {
          key: "FO_MEETING_V2_PROG_SUBTITLE",
          label: "Sous-titre",
          type: "text",
          multiline: true,
          fallback: "Les conférences et ateliers de l'événement.",
          target: "progTitle",
        },
        {
          key: "FO_MEETING_V2_PROG_HTML",
          label: "Message d'introduction",
          type: "html",
          target: "progTitle",
          help: "Affiché sous le titre, avant le programme, en HTML libre. Vide : rien ne s'affiche.",
        },
      ],
    },
  ],

  badge: [
    /* Le titre de cette page est son NOM (« Pages du site ») : il n'est pas ici, mais
       il se modifie aussi sur place dans la page (tâche #557). Le sous-titre, lui, est
       un réglage comme sur les autres écrans (tâche #552 : c'était un mot du produit,
       que l'organisateur ne pouvait pas changer). */
    {
      title: "Introduction",
      settings: [
        {
          key: "FO_MEETING_V2_BADGE_SUBTITLE",
          label: "Sous-titre",
          type: "text",
          multiline: true,
          fallback: "Présentez ce badge à l'entrée.",
          target: "badgeTitle",
        },
        {
          key: "FO_MEETING_V2_BADGE_HTML",
          label: "Message d'introduction",
          type: "html",
          target: "badgeTitle",
          help: "Affiché sous le titre, avant la carte, en HTML libre. Vide : rien ne s'affiche.",
        },
      ],
    },
    {
      title: "Contenu du badge",
      help: "Ce que porte la carte, en plus du nom. Le QR encode l'identifiant réel de l'inscription : c'est celui que lit le scan de l'accueil.",
      settings: [
        {
          key: "FO_MEETING_V2_BADGE_QR",
          label: "Afficher le QR code",
          type: "boolean",
          onByDefault: true,
          target: "badgeCard",
          help: "Le même code que sur le badge imprimé. Sans lui, il reste l'identifiant à dicter.",
        },
        {
          key: "FO_MEETING_V2_BADGE_COMPANY",
          label: "Afficher société et fonction",
          type: "boolean",
          onByDefault: true,
          target: "badgeCard",
          help: "Rien ne s'affiche pour qui n'est rattaché à aucune société : un salon grand public peut laisser ce réglage allumé sans conséquence.",
        },
        {
          key: "FO_MEETING_V2_BADGE_ID",
          label: "Afficher l'identifiant",
          type: "boolean",
          onByDefault: true,
          target: "badgeCard",
          help: "C'est ce qu'on dicte à l'accueil quand le QR ne passe pas.",
        },
        /**
         * ⚠️ THE PROTOTYPE HAS ONE MORE SETTING HERE, "Identifiant affiché", AND IT IS
         * NOT SHIPPED. It is free text, the same for everybody: in a demonstration with
         * no database, something had to be written under the QR. In reality the
         * identifier belongs to the PERSON (`UserEvents.code`) — a setting would have
         * replaced it with one string across every badge, and the desk would have heard
         * everybody read out the same number.
         */
        {
          key: "FO_MEETING_V2_BADGE_NOTICE",
          label: "Mention sous le badge",
          type: "text",
          target: "badgeCard",
          help: "Vide : aucune mention. Par exemple « Badge personnel, il ne se prête pas ».",
        },
      ],
    },
    {
      title: "Actions",
      settings: [
        {
          key: "FO_MEETING_V2_BADGE_PDF",
          label: "Bouton « Télécharger en PDF »",
          type: "boolean",
          onByDefault: true,
          target: "badgeActions",
          /* The help states the CONDITION, because it lives outside this panel: with no
             template the server refuses to render, and the organiser would look for the
             cause here. */
          help: "Rend le badge dessiné dans le back office. Il faut qu'un modèle de badge soit configuré pour l'événement, sinon le téléchargement échoue et le dit.",
        },
        {
          key: "FO_MEETING_V2_BADGE_SEND_SHOW",
          origin: {
            key: "FO_MEETING_CONFIRMATION_SHOW_SEND_BADGE",
            note: "Vérifié : `screens/steps/Confirmation/index.jsx` de la V1 pose « Envoyer le badge » sur la carte d'un accompagnant qui a une adresse, et envoie l'action 4. La V2 le pose sur l'écran badge, à côté du téléchargement de chaque accompagnant.",
          },
          label: "Bouton « Envoyer le badge » d'un accompagnant",
          type: "boolean",
          target: "badgeActions",
          help: "Sur la carte de chaque accompagnant : lui envoie son badge par email (action « Badge à télécharger »). Il faut un email réglé pour cette action, sinon le bouton le dit.",
        },
        {
          key: "FO_MEETING_V2_BADGE_SEND_LABEL",
          needs: "FO_MEETING_V2_BADGE_SEND_SHOW",
          label: "Libellé du bouton",
          type: "text",
          fallback: "Envoyer le badge",
          target: "badgeActions",
        },
        {
          key: "FO_MEETING_V2_EMAIL_BADGE",
          needs: "FO_MEETING_V2_BADGE_SEND_SHOW",
          label: "Email qui porte le badge",
          type: "action-email" as const,
          emailAction: "badge",
          target: "badgeActions",
          translatable: false,
          help: "Le même réglage que la page « Templates email » du back office : le compte qui envoie, et le modèle de chaque langue. Sans modèle, rien ne part et l'inscription se passe de lui. La variable {{var:token}} permet un lien qui ouvre l'écran badge directement connecté.",
        },
      ],
    },
  ],

  /**
   * UNE SECTION : le HTML de l'organisateur, habillé par le produit.
   *
   * ⚠️ UNE FAMILLE POUR TOUS LES EXEMPLAIRES : la page se multiplie (« autant que
   * l'on veut sur l'accueil »), et c'est l'INSTANCE qui suffixe les clés : la même
   * mécanique que l'étape informations, pour la même raison : écrite en dur, la clé
   * de la première recevrait le contenu de toutes les autres. Le NOM ne se règle pas
   * ici : il vit sur sa ligne du gestionnaire « Pages du site », comme celui de
   * chaque page.
   *
   * ⚠️ PAS D'`origin` : les sections de la V1 sont des DONNÉES (la table
   * `Sections`, éditée au back office), pas des clés de réglage, la reprise n'a rien
   * à transporter. Et `FO_MEETING_FILES_HTML` n'est PAS un candidat : c'est l'écran
   * des documents (`screens/files` de la V1), déjà vérifié au moment de la reprise.
   */
  infos: [
    {
      title: "Section",
      help: "Une page libre : son contenu est le vôtre, en HTML. Le produit apporte le châssis : sommaire dérivé de vos titres, tableaux lisibles sur téléphone, repli de langue annoncé au lecteur.",
      settings: ({ instance }) => [
        {
          key: subtitleKey(INFOS_PAGE, instance),
          label: "Sous-titre",
          type: "text" as const,
          multiline: true,
          /* Aucun repli, pour la première section comme pour les suivantes : une
             description écrite par le produit sous un contenu écrit par
             l'organisateur ne peut que finir par mentir. */
          target: "infosTitle",
          help: "Sous le nom de la page, et sur sa tuile de l'accueil.",
        },
        {
          key: htmlKey(INFOS_PAGE, instance),
          label: "Contenu de la page",
          type: "html" as const,
          target: "infosBody",
          help: "Servi tel quel, dans le cadre de la page. Vos titres (h3) deviennent le sommaire ; vos tableaux reçoivent un cadre qui défile sur téléphone.",
        },
      ],
    },
  ],

  /**
   * UNE PAGE FORMULAIRE — un formulaire du back office, servi hors du tunnel.
   *
   * « Comme les sections, on peut ajouter des pages qui comportent un formulaire que
   * l'on configure » (2026-08-20). Même grammaire que la section : une famille pour
   * TOUS les exemplaires, et c'est l'INSTANCE qui suffixe les clés — écrite en dur,
   * celle de la première recevrait le formulaire de toutes les autres. Le NOM ne se
   * règle pas ici : il vit sur sa ligne du gestionnaire « Pages du site ».
   *
   * ⚠️ ELLE REMPLACE LA PAGE « SATISFACTION », qui était cela en un exemplaire et sous
   * un nom fixe. D'où l'`origin` sur la PREMIÈRE page seulement : le formulaire de
   * satisfaction de la V1 (`FO_MEETING_SATISFACTION_FORM_ID`,
   * `screens/SatisfactionForm.jsx`) arrive dans la première page formulaire, ce que la
   * reprise sait faire. Les suivantes n'ont pas d'origine — la V1 n'en a qu'une.
   *
   * ⚠️ PAS UNE ÉTAPE DU PARCOURS, et c'est ce qui la distingue d'« informations » : on
   * ne demande pas son avis à quelqu'un qui n'est pas encore inscrit.
   */
  form: [
    {
      title: "Page formulaire",
      help: "Une page qui sert un formulaire du back office : la personne y répond, corrige et renvoie. Son nom et sa place se règlent dans « Pages du site ».",
      settings: ({ instance, forms }) => [
        {
          key: subtitleKey(FORM_PAGE, instance),
          label: "Sous-titre",
          type: "text" as const,
          multiline: true,
          target: "formTitle",
          help: "Sous le nom de la page, et sur sa tuile de l'accueil.",
        },
        {
          key: htmlKey(FORM_PAGE, instance),
          label: "Message d'introduction",
          type: "html" as const,
          target: "formTitle",
          help: "Affiché au-dessus du formulaire, en HTML libre. Vide : rien ne s'affiche.",
        },
        {
          key: formIdKey(FORM_PAGE, instance),
          /* ⚠️ L'ORIGINE NE VAUT QUE POUR LA PREMIÈRE. La V1 n'a qu'un formulaire hors
             tunnel, celui de la satisfaction : le proposer à la reprise sur chaque
             exemplaire ajouté ferait servir le même questionnaire par trois pages. */
          ...(instance && instance !== FIRST_INSTANCE
            ? {}
            : {
                origin: {
                  key: "FO_MEETING_SATISFACTION_FORM_ID",
                  note: "Le back office l'appelle « Satisfaction ». La V2 n'a plus de page satisfaction : c'est une page formulaire, et celle-ci est la première.",
                },
              }),
          label: "Formulaire servi",
          type: "select-forms-user" as const,
          target: "formForm",
          /* Les formulaires de PARTICIPANT seulement, comme l'étape informations : un
             questionnaire de société n'aurait personne à qui poser ses questions ici. */
          options: formsOf(forms, "U"),
          help: "Un formulaire de participant. Ses questions se créent dans le back office ; sa présentation ici se règle juste en dessous.",
        },
        {
          key: instanceKey("FO_MEETING_V2_FORM_FIELDS", instance),
          label: "Présentation des champs",
          type: "form-editor" as const,
          // QUEL formulaire cet éditeur ouvre : celui que la ligne du dessus désigne.
          formKey: formIdKey(FORM_PAGE, instance),
          target: "formForm",
          translatable: false,
          help: "Le type, la largeur et l'ordre des champs, dans ce formulaire. Les questions elles-mêmes restent au back office : les renommer ici les renommerait pour tous les formulaires du domaine.",
        },
      ],
    },
  ],
};

/** Resolves a group's settings, generating them when they depend on the event. */
function resolve(
  source: Group["settings"],
  context?: CatalogContext,
): ReadonlyArray<Setting> {
  if (typeof source !== "function") return source;
  // No context: nothing can be generated, so the group contributes nothing rather
  // than being invented from a guess.
  return context ? source(context) : [];
}

/**
 * The settings that drive ONE element of the page, found from its `data-cfg`.
 *
 * This is the reverse of what the panel does. The panel goes from the catalog to
 * the page ("this row drives that element"); clicking an element goes from the
 * page back to the catalog ("what governs what I just pointed at").
 *
 * Both directions read the SAME `target` field, so they cannot disagree — and a
 * setting that gains a target becomes clickable on the page without anyone
 * wiring anything.
 *
 * `site` first, then the screen: an element carrying a target found in both gets
 * the shared settings before its own, which is the order the panel shows them in.
 */
/**
 * LE NOM LISIBLE D'UN BLOC, depuis son `data-cfg`.
 *
 * ⚠️ C'EST LE TITRE DE SA FAMILLE, comme sur la carte. « Titre de l'étape » se relit ;
 * `[data-cfg="infoTitle"] h2` demande un effort à chaque lecture, et la ligne qui
 * liste les règles est justement celle qu'on parcourt pour retrouver ce qu'on a fait.
 */
/**
 * Le nom qu'une carte de bloc donne à sa famille : celui de la famille, ou celui de la
 * SECTION quand la famille est faite d'anciennes familles (`sectioned`). Le bandeau du
 * haut ouvre « Haut de page », comme avant le regroupement dans « Apparence ».
 */
function cardTitle(group: Group, setting: Setting): string {
  return group.sectioned && setting.section ? setting.section : group.title;
}

export function labelForTarget(
  target: string,
  context: CatalogContext = WITHOUT_EVENT,
): string | null {
  for (const groups of Object.values(CATALOG))
    for (const group of groups) {
      // Les groupes engendrés comptent aussi : les blocs des étapes informations et
      // société y sont déclarés, ET ceux ENGENDRÉS PAR TYPOLOGIE (« Typologies proposées »).
      // Résolus avec un événement vide, leur cible n'a pas de nom et la liste des règles
      // affiche « [data-cfg="typoOptions"] » au lieu de sa famille — d'où le contexte.
      const settings = resolve(group.settings, context);
      const found = settings.find((s) => s.target === target);
      if (found) return cardTitle(group, found);
    }
  return null;
}

/**
 * LE BLOC TITRE DES PAGES MULTIPLIABLES, et ce qu'il affiche : le NOM de la page.
 *
 * ⚠️ CE NOM SE RÈGLE DANS « PAGES DU SITE », ET NULLE PART AILLEURS — il y est déclaré
 * `managed`, ce que la carte au survol filtre. Sur une section, survoler le titre
 * n'offrait donc que le sous-titre : le titre lui-même, pourtant sous le curseur, ne
 * se changeait que depuis le tableau des pages. Relevé par William le 2026-09-13.
 */
const PAGE_TITLE_TARGET: Readonly<Record<string, string>> = {
  infos: "infosTitle",
  form: "formTitle",
};

/**
 * Le réglage du nom de la page regardée, prêt pour la carte : la même clé que la
 * ligne du gestionnaire (une seule valeur, deux portes), mais sans `managed`, pour
 * qu'elle s'affiche là où l'on survole le titre.
 */
function pageNameSetting(
  page: string,
  target: string,
  instance?: string,
): Setting | null {
  if (PAGE_TITLE_TARGET[page] !== target) return null;
  const base = REPEATABLE_PAGES.find((p) => p.key === page);
  if (!base) return null;
  const shown = pageOfInstance(base, instance);
  return {
    key: labelKey(shown),
    label: "Nom de la page",
    type: "text",
    fallback: shown.name,
    target,
    help: "Le titre de cette page, aussi dans le menu et sur sa tuile de l'accueil. Se règle aussi dans « Pages du site ».",
  };
}

export function settingsForTarget(
  page: string,
  target: string,
  context?: CatalogContext,
): { title: string; settings: ReadonlyArray<Setting> } | null {
  const groups = [...(CATALOG.site ?? []), ...(CATALOG[page] ?? [])];
  const found = groups.flatMap((group) =>
    resolve(group.settings, context)
      .filter((setting) => setting.target === target)
      .map((setting) => ({ group: cardTitle(group, setting), setting })),
  );
  if (found.length === 0) return null;
  const name = pageNameSetting(page, target, context?.instance);
  return {
    // The group's own title: it already names the family the organiser knows
    // ("Identité", "Pied de page"), so there is nothing new to learn.
    title: found[0]!.group,
    // Le nom en tête : c'est ce qu'on lit en premier sur le bloc survolé.
    settings: [...(name ? [name] : []), ...found.map((f) => f.setting)],
  };
}

/**
 * The settings worth showing right now: those whose switch is on.
 *
 * ⚠️ ONE implementation, used by the right-hand panel AND by the card that opens
 * beside a block. Two would drift, and the symptom would be a field visible in
 * one place and gone in the other — with no way to tell which is telling the
 * truth.
 */
/** Les conditions d'un réglage, toujours en liste (une chaîne seule en est une de un). */
export function needsOf(setting: Setting): ReadonlyArray<string> {
  const { needs } = setting;
  if (!needs) return [];
  return typeof needs === "string" ? [needs] : needs;
}

export function visibleSettings(
  settings: ReadonlyArray<Setting>,
  read: (key: string) => unknown,
): ReadonlyArray<Setting> {
  const byKey = new Map(settings.map((s) => [s.key, s]));

  /**
   * Un réglage est visible si son interrupteur l'autorise — et si CET interrupteur est
   * lui-même visible.
   *
   * ⚠️ LA VISIBILITÉ SE PROPAGE, ET NE LE FAISAIT PAS. Chaque réglage était jugé seul :
   * « Libellé du lien » dépend de « Lien mot de passe oublié », qui est allumé par
   * défaut, donc il restait affiché alors que son propre interrupteur venait d'être
   * masqué par « Connexion par lien envoyé par e-mail ». On réglait le libellé d'un
   * lien qui ne peut pas s'afficher.
   *
   * ⚠️ ET LA CHAÎNE EST GARDÉE CONTRE ELLE-MÊME. Deux réglages qui se réclameraient
   * l'un l'autre boucleraient à l'infini ; la trace des clés déjà vues arrête net et
   * répond « visible », le moindre mal : on montre un réglage de trop plutôt que de
   * figer le panneau.
   */
  const visible = (setting: Setting, seen: Set<string>): boolean => {
    if (!setting.needs) return true;
    if (seen.has(setting.key)) return true;
    /* Plusieurs conditions : TOUTES doivent tenir. Voir `needs` dans `types.ts`. */
    return needsOf(setting).every((need) => holds(setting, need, seen));
  };

  const holds = (
    setting: Setting,
    need: string,
    seen: Set<string>,
  ): boolean => {
    const negated = need.startsWith("!");
    const key = negated ? need.slice(1) : need;

    const master = byKey.get(key);
    if (master && !visible(master, new Set(seen).add(setting.key)))
      return false;

    // The EFFECTIVE state of the switch, absent key included: a family gated by
    // a switch that is on by default must be shown while nobody has touched it,
    // or the settings behind it are unreachable on a fresh event.
    const raw = read(key);
    if (typeof raw === "boolean") return negated ? !raw : raw;
    /* Un maître qui porte une VALEUR — une image — tient quand elle est posée : le voile
       d'une image de fond n'a rien à régler tant qu'il n'y a pas d'image. */
    if (typeof raw === "string") return negated ? raw.trim() === "" : raw.trim() !== "";
    /**
     * ⚠️ THE MASTER MAY BE A GENERATED KEY, AND THE STATIC INDEX CANNOT KNOW IT.
     * `defaultOnOf` reads `BY_KEY`, built by walking the catalog with an EMPTY event:
     * `FO_MEETING_V2_TYPO_SHOW_538` exists only for an event that owns typology 538, so
     * the lookup missed and its default fell back to "off". Measured on the typology
     * step: every switch drawn ON, and the panel below them announcing "1 réglage est
     * masqué tant que « Proposée » est désactivé" — three times.
     *
     * The list at hand holds the master, so it is asked FIRST. Nothing changes for a
     * declared pair, and every generated one is covered — the days of an event and
     * whatever comes next included.
     */
    const on = master ? master.onByDefault === true : defaultOnOf(key);
    return negated ? !on : on;
  };

  return settings.filter((s) => visible(s, new Set()));
}

/**
 * Every catalogued setting that can be TRANSLATED, across every screen.
 *
 * Used by the "translate everything" button. Prose only: a name, an address, an
 * email or a colour is excluded by `translatable: false`, and handing any of them
 * to a model is how a support address stops working.
 *
 * ⚠️ AN HTML BLOCK IS PROSE TOO. An introduction is exactly what a multilingual
 * event needs translated, and the route's prompt already says what to do with
 * markup — translate the text, leave the tags, their attributes and their order
 * strictly intact. Excluding it would leave one screen's welcome in French for
 * everybody, with no way to fix it but writing the markup out again per language.
 */
export function translatableSettings(
  steps?: ReadonlyArray<Entry>,
  typos: CatalogContext["typos"] = [],
  /** Les rangs des pages ajoutées, voir `everySetting`. */
  pages: ReadonlyArray<string> = [],
): ReadonlyArray<Setting> {
  return everySetting(steps, typos, pages).filter(
    (s) => TRANSLATABLE_TYPES.has(s.type) && s.translatable !== false,
  );
}

/** The types whose value is prose in some language. */
export const TRANSLATABLE_TYPES: ReadonlySet<Setting["type"]> = new Set([
  "text",
  "html",
]);

/**
 * The types whose control is ONE CHOICE among options the event supplies.
 *
 * ⚠️ They were declared and catalogued but never RENDERED: the row showed its label,
 * its help and its provenance, and no control at all — so the form of the
 * informations step could not be chosen from the panel, only guessed at. They all
 * draw the same way; what differs is where the options come from, which is the
 * catalog's business, not the row's.
 */
export const CHOICE_TYPES: ReadonlySet<Setting["type"]> = new Set([
  "select-forms-user",
  "select-forms-company",
  "select-badges",
  "select-badge-template",
  "select-settings",
]);

/**
 * UN ÉVÉNEMENT VIDE — de quoi PARCOURIR un groupe engendré sans en connaître aucun.
 *
 * Une ligne dont l'existence dépend de l'événement (un interrupteur par typologie)
 * disparaît alors, ce qui est exact : sans événement, elle n'existe pas. Une ligne
 * DÉCLARÉE dans un groupe engendré — un titre, un sous-titre, le formulaire servi —
 * reste, avec son repli et son type, qui ne dépendent d'aucun événement.
 */
const WITHOUT_EVENT: CatalogContext = { typos: [], forms: [] };

/**
 * Every setting the catalog declares, all screens together.
 *
 * ⚠️ LES GROUPES ENGENDRÉS SONT PARCOURUS, avec un événement vide — et les ignorer
 * était un bug silencieux, pas une simplification. Les deux lecteurs de cette liste
 * cherchent un repli déclaré à la main (`fallbackOf`) et les chaînes à traduire
 * (`translatableSettings`) : l'étape informations déclare ses trois textes dans un
 * groupe engendré (il lui faut la liste des formulaires), donc son titre retombait
 * sur la CHAÎNE VIDE — un `<h2>` blanc sur un événement neuf — et « Tout traduire »
 * sautait le titre, le sous-titre et l'introduction de cet écran sans le dire.
 *
 * Un réglage engendré porte son propre `onByDefault`, que `Row` lit directement.
 */
export function everySetting(
  /**
   * ⚠️ LE PARCOURS RANGÉ, QUAND ON L'A — sinon la liste s'arrête à la PREMIÈRE instance.
   * Un événement qui sert deux étapes « informations » déclare deux titres, deux
   * sous-titres, deux introductions et deux noms d'étape ; l'événement vide n'en connaît
   * qu'un jeu. « Tout traduire » sautait donc en silence tout ce que porte une étape
   * ajoutée — configurable, mais intraduisible.
   */
  steps?: ReadonlyArray<Entry>,
  /**
   * ⚠️ LES TYPOLOGIES DE L'ÉVÉNEMENT, sinon les réglages ENGENDRÉS PAR TYPOLOGIE — la
   * précision d'une typologie (`TYPO_NOTE_<id>`), un texte traduisible — manquent, et
   * « Tout traduire » les saute en silence. Vides par défaut : l'ancien comportement.
   */
  typos: CatalogContext["typos"] = [],
  /**
   * ⚠️ LES RANGS DES PAGES AJOUTÉES, familles confondues (voir `addedInstances`), une
   * occurrence de plus du même piège : tout index bâti sur un événement vide ignore les
   * clés engendrées. Sans eux, le sous-titre et le contenu d'une page ajoutée seraient
   * configurables mais intraduisibles, exactement comme ceux d'une étape ajoutée avant
   * ce paramètre.
   */
  pages: ReadonlyArray<string> = [],
): ReadonlyArray<Setting> {
  /* Les instances à parcourir : la première (l'événement vide la donne déjà) puis
     chacune des suivantes, une passe par instance — c'est `instance` qui suffixe les
     clés, et il est singulier par construction.

     ⚠️ ÉTAPES ET PAGES CONFONDUES, et c'est assumé : une passe d'instance « 2 » engendre
     les clés suffixées des DEUX familles répétables, même quand une seule a cet
     exemplaire. La clé fantôme est inerte (sans valeur, « Tout traduire » la saute ;
     dans l'index, elle ne fait qu'exister) et le tri fin coûterait à chaque lecteur la
     connaissance de qui possède quelle instance. */
  const next = [
    ...new Set([
      ...(steps ?? [])
        .map((e) => e.instance)
        .filter((i): i is string => !!i && i !== FIRST_INSTANCE),
      ...pages.filter((i) => i !== FIRST_INSTANCE),
    ]),
  ];
  const base: CatalogContext = { typos, forms: [] };
  const contextes: ReadonlyArray<CatalogContext> = [
    { ...base, ...(steps ? { steps } : {}) },
    ...next.map((instance) => ({
      ...base,
      instance,
      ...(steps ? { steps } : {}),
    })),
  ];

  const seen = new Set<string>();
  return (
    Object.values(CATALOG)
      .flatMap((groups) => groups ?? [])
      .flatMap((group) => contextes.flatMap((c) => resolve(group.settings, c)))
      /* Une passe par instance revoit tout ce qui ne dépend pas d'elle : sans ce
       dédoublonnage, chaque réglage commun serait compté — et traduit — deux fois. */
      .filter((s) => !seen.has(s.key) && seen.add(s.key))
  );
}

/**
 * TOUT LE CATALOGUE, groupes ENGENDRÉS COMPRIS — ce qu'on envoie pour faire
 * correspondre les réglages de la V1 aux nôtres.
 *
 * ⚠️ `everySetting()` ne sait pas parcourir un groupe engendré (il dépend de
 * l'événement), et c'est sans conséquence pour ses deux lecteurs. Ici ça en aurait
 * une : les interrupteurs d'étape et le formulaire servi sont engendrés, donc la
 * reprise ne les aurait jamais proposés — l'organisateur les ressaisirait à la main
 * sans savoir pourquoi ceux-là et pas les autres. Le contexte, lui, est disponible au
 * moment où on appelle : le panneau le porte déjà.
 */
export function fullCatalog(context: CatalogContext): ReadonlyArray<Setting> {
  return [
    ...Object.values(CATALOG)
      .flatMap((groups) => groups ?? [])
      .flatMap((group) =>
        typeof group.settings === "function"
          ? group.settings(context)
          : group.settings,
      ),
    ...PAGE_MIGRATIONS,
  ];
}

/**
 * ═══ LES PAGES DU SITE, POUR LA REPRISE SEULEMENT ═══
 *
 * ⚠️ CES LIGNES N'APPARAISSENT PAS DANS LE PANNEAU, et c'est voulu : les pages s'y
 * règlent d'un seul geste, dans le gestionnaire « Pages du site » (une ligne par
 * page, trois clés derrière). Les redéclarer comme des réglages ordinaires
 * afficherait quarante-deux interrupteurs à côté du gestionnaire qui les pilote —
 * exactement la contradiction que ce gestionnaire existe pour supprimer.
 *
 * Mais la REPRISE, elle, ne connaît que le catalogue qu'on lui envoie : une cible
 * absente est refusée (« clé V2 inconnue du catalogue »). Sans ces lignes, un
 * événement V1 arrivait avec ses modules allumés — photos, médias, contacts,
 * badge — et repartait avec un accueil vide. Mesuré sur l'événement de test :
 * cinq tuiles dans la V1, zéro dans la V2, et rien ne le disait.
 *
 * ⚠️ LA TUILE, PAS LE MENU. La V1 n'a AUCUN menu de pages — sa barre du haut ne
 * porte que le retour à l'accueil, la langue et la déconnexion (`shared/navbar.jsx`) ;
 * toute sa navigation passe par les tuiles de l'accueil. Reprendre un de ses
 * drapeaux dans une entrée de menu serait inventer une intention. Le menu, lui, est
 * proposé par la complétion, qui le DIT.
 */
const PAGE_MIGRATIONS: ReadonlyArray<Setting> = [
  {
    key: tileKey({ key: "album" }),
    label: "Album en tuile sur l'accueil",
    type: "boolean",
    origin: {
      key: "FO_MEETING_SHOW_PHOTOS",
      note: "Vérifié : `screens/Home.jsx` de la V1 n'affiche la tuile « Mes photos » (route PHOTOS) que si ce drapeau est vrai.",
    },
  },
  {
    key: tileKey({ key: "contacts" }),
    label: "Contacts en tuile sur l'accueil",
    type: "boolean",
    origin: {
      key: "FO_MEETING_USER_LINKS_IS_ACTIVE",
      note: "Vérifié : `screens/Home.jsx` de la V1 en tire DEUX tuiles, « Voir mes contacts » et « Scanner un badge ». Reprise sur les contacts, le carnet lui-même ; le scan est un écran d'exposant que l'organisateur allume s'il le veut.",
    },
  },
  {
    key: tileKey({ key: "badge" }),
    label: "Badge en tuile sur l'accueil",
    type: "boolean",
    origin: {
      key: "FO_MEETING_HOME_SHOW_DOWNLOAD_BADGE",
      note: "Vérifié : `screens/Home.jsx` de la V1 conditionne la tuile « Mon badge » à ce drapeau (avec un gabarit de badge et une inscription réglée, qui sont l'état du participant, pas un réglage).",
    },
  },
  {
    key: tileKey({ key: "programme" }),
    label: "Programme en tuile sur l'accueil",
    type: "boolean",
    origin: {
      key: "FO_MEETING_ACTIVE_CONFERENCE",
      note: "Vérifié : `screens/Home.jsx` de la V1 conditionne la tuile « Mes conférences » à ce drapeau. À ne pas confondre avec `FO_MEETING_CONFERENCES_V2_IS_ACTIVE`, qui ajoute une ÉTAPE au tunnel (`screens/steps/Steps.jsx`), pas une page.",
    },
  },
];

const BY_KEY = new Map(everySetting().map((s) => [s.key, s] as const));

/**
 * What the SITE displays for a setting nobody filled in.
 *
 * Declared ONCE, in the catalog, and read from there by the page. Written twice
 * — once in the entry, once in the component — the two drift, and the panel then
 * promises a default the footer does not use.
 */
export function fallbackOf(key: string): string {
  return BY_KEY.get(key)?.fallback ?? "";
}

/** Un réglage déclaré, d'où qu'il soit : pour dessiner celui qu'une ligne porte (`labelKey`). */
export function settingOf(key: string): Setting | undefined {
  return BY_KEY.get(key);
}

/** Les deux clés que pilote « Mode de connexion » (type `login-mode`). */
export const LOGIN_LINK_KEY = "FO_MEETING_V2_LOGIN_AUTO_CONNECT";
export const LOGIN_OTP_KEY = "FO_MEETING_V2_LOGIN_OTP";
/** Vrai : personne ne peut se connecter (formulaire retiré, bouton de la barre aussi). */
export const LOGIN_DISABLED_KEY = "FO_MEETING_V2_LOGIN_DISABLED";
/**
 * Les clés que le panneau ne montre pas comme des lignes mais qu'un `needs` peut
 * nommer : la note « masqué par … » doit alors désigner la ligne qui les pilote.
 */
const DRIVEN_BY: Record<string, string> = {
  [LOGIN_LINK_KEY]: "FO_MEETING_V2_LOGIN_MODE",
  [LOGIN_OTP_KEY]: "FO_MEETING_V2_LOGIN_MODE",
  [LOGIN_DISABLED_KEY]: "FO_MEETING_V2_LOGIN_MODE",
};

/** Les options de « Mode de connexion », dans l'ordre du sélecteur. */
export const LOGIN_MODES = [
  { value: "disabled", label: "Désactivée" },
  { value: "password", label: "Mot de passe" },
  { value: "link", label: "Lien envoyé par email" },
  { value: "otp", label: "Code envoyé par email" },
] as const;
export type LoginMode = (typeof LOGIN_MODES)[number]["value"];

/** Le mode rangé, lu dans ses trois clés : la désactivation prime, puis le lien, puis le code. */
export function loginModeOf(read: (key: string) => unknown): LoginMode {
  if (read(LOGIN_DISABLED_KEY) === true) return "disabled";
  if (read(LOGIN_LINK_KEY) === true) return "link";
  if (read(LOGIN_OTP_KEY) === true) return "otp";
  return "password";
}

/**
 * ⚠️ UNE CLÉ PILOTÉE PAR UN SÉLECTEUR N'EST NI ALLUMÉE NI ÉTEINTE pour l'organisateur :
 * il voit « Mode de connexion », pas trois booléens. La note d'un réglage masqué doit
 * donc nommer l'OPTION choisie (« Code envoyé par email »), et le vocabulaire
 * activé/désactivé d'un interrupteur y mentait : « Mode de connexion est désactivé »,
 * en mode code. Rend la ligne qui pilote la clé, son libellé et l'option qu'elle
 * affiche, ou rien si la clé n'est pas pilotée.
 */
export function drivenOptionOf(
  key: string,
  read: (key: string) => unknown,
): { driver: string; label: string; option: string } | null {
  const driver = DRIVEN_BY[key];
  if (!driver) return null;
  const mode = loginModeOf(read);
  const option = LOGIN_MODES.find((m) => m.value === mode)!.label;
  return { driver, label: BY_KEY.get(driver)?.label ?? "", option };
}

/** A setting's organiser-facing label, from anywhere in the catalog. */
export function labelOf(key: string): string {
  return (
    BY_KEY.get(key)?.label ?? BY_KEY.get(DRIVEN_BY[key] ?? "")?.label ?? ""
  );
}

/** Whether an absent switch counts as ON — see `onByDefault`. */
export function defaultOnOf(key: string): boolean {
  return BY_KEY.get(key)?.onByDefault === true;
}
